class HpCollection extends HTMLElement {
  constructor() {
    super();
    this.products = [];
    this.productMap = new Map();
    this.cardMap = new Map();
    this.activeProduct = null;
    this.activeImageIndex = 0;
    this.lastFocusedElement = null;
    this.buyingGuideLastFocusedElement = null;
    this.scrollLockY = 0;
    this.previousScrollBehavior = '';
    this.previousDocumentOverflow = '';
    this.previousDocumentOverscrollBehavior = '';
    this.previousDocumentScrollbarGutter = '';
    this.previousBodyOverflow = '';
    this.previousBodyOverscrollBehavior = '';
    this.previousBodyPaddingRight = '';
    this.modalSwipeStartX = 0;
    this.modalSwipeStartY = 0;
    this.modalSwipePointerId = null;
    this.modalImageSwipe = null;
    this.cardImagePreloadObserver = null;
    this.loadedModalImages = new Set();
    this.modalImagePreloadPromises = new Map();
    this.productDetailRequests = new Map();
    this.productDetailPreloadObserver = null;
    this.productDetailPreloadQueue = [];
    this.activeProductDetailPreloads = 0;
    this.backgroundProductDetailPreloadCount = 0;
    this.productDetailWarmupStarted = false;
    this.modalImageRequestId = 0;
    this.modalImages = [];
    this.activeModalCardImageSrcs = [];
    this.modalTrackScrollFrame = 0;
    this.modalViewportFrame = 0;
    this.persistentScrollbarFrame = 0;
    this.persistentScrollAreas = [];
    this.persistentScrollbarMutationObserver = null;
    this.persistentScrollbarResizeObserver = null;
    this.collectionPageLoadRequest = null;
    this.loadedCollectionPageUrls = new Set();
    this.latestCart = null;
    this.cartQuantityRequest = null;
    this.cartStateVersion = 0;
    this.sizeAliasRules = [];
    this.memberDiscount = 0;
    this.memberTier = 'Member';
    this.boundKeydown = (event) => this.handleKeydown(event);
    this.boundOutsidePointerdown = (event) => this.handleOutsidePointerdown(event);
    this.boundCartStateUpdate = (event) => this.handleCartStateUpdate(event);
    this.boundModalTouchStart = (event) => this.captureModalTouchStart(event);
    this.boundModalScrollBlock = (event) => this.blockModalBackgroundScroll(event);
    this.boundModalImageSwipeStart = (event) => this.captureModalImageSwipeStart(event);
    this.boundModalImageSwipeMove = (event) => this.trackModalImageSwipe(event);
    this.boundModalImageSwipeEnd = (event) => this.handleModalImageSwipeEnd(event);
    this.boundModalImageSwipeCancel = () => {
      this.resetModalImageSwipe();
      this.updateModalImageControls(this.modalImages, this.activeImageIndex);
    };
    this.boundModalTrackScroll = () => this.syncModalImageFromTrack();
    this.boundModalViewportChange = () => this.scheduleModalViewportSync();
    this.boundModalImageWheel = (event) => this.handleModalImageWheel(event);
    this.boundModalImageNativeDragStart = (event) => event.preventDefault();
    this.boundPersistentScrollbarScroll = () => this.schedulePersistentScrollbarSync();
    this.boundPersistentScrollbarResize = () => this.schedulePersistentScrollbarSync();
    this.boundSizePanelViewportChange = () => {
      if (this.isSizePanelOpen()) this.updateSizePanelPosition();
    };
    this.boundUrlPopState = () => {
      this.applyStateFromUrl();
      this.applyFilters({ updateUrl: false });
    };
    this.boundPageShow = (event) => {
      if (!event?.persisted) return;
      this.resetModalState();
      this.resetBuyingGuideState();
    };
    this.boundStartProductDetailWarmup = () => this.startProductDetailWarmup();
    this.modalTouchX = 0;
    this.modalTouchY = 0;
    this.lastModalWheelTime = 0;
    this.lastSelectedSizes = [];
  }

  connectedCallback() {
    if (this.dataset.hpReady === 'true' || this.dataset.hpReady === 'refreshing') return;
    if (this.shouldRefreshStaleSection()) {
      this.dataset.hpReady = 'refreshing';
      this.refreshStaleSection().then((refreshed) => {
        if (!refreshed && this.isConnected) {
          delete this.dataset.hpReady;
          this.initializeCollection();
        }
      });
      return;
    }

    this.initializeCollection();
  }

  initializeCollection() {
    if (this.dataset.hpReady === 'true') return;
    this.dataset.hpReady = 'true';
    document.body.classList.add('hp-collection-active');

    this.products = [];
    this.productMap.clear();
    this.memberDiscount = Math.max(0, Math.min(100, Number(this.dataset.memberDiscount || 0) || 0));
    this.memberTier = this.dataset.memberTier || 'Member';
    this.sizeAliasRules = this.parseSizeAliasRules(this.dataset.sizeAliases);
    this.registerProducts(this.parseProducts());

    this.cacheElements();
    this.cacheCards();
    this.resetModalState();
    this.resetBuyingGuideState();
    this.setPricePlaceholders();
    this.populateSizeFilter();
    this.applyStateFromUrl();
    this.bindEvents();
    this.setupPersistentScrollbars();
    this.applyFilters({ updateUrl: false });
    this.loadRemainingCollectionPages();
    window.addEventListener('pageshow', this.boundPageShow);
    window.addEventListener('popstate', this.boundUrlPopState);
    document.addEventListener('cart:update', this.boundCartStateUpdate);
    document.addEventListener('cart:refresh', this.boundCartStateUpdate);
    this.refreshCartQuantities({ updateButtons: true });
    if (document.readyState === 'complete') this.startProductDetailWarmup();
    else window.addEventListener('load', this.boundStartProductDetailWarmup, { once: true });

    // Keep collection cards lightweight; the popup owns multi-image browsing.
  }

  shouldRefreshStaleSection() {
    return !this.querySelector('[data-hp-button-label]') && !!this.querySelector('[data-hp-card-add] .hp-btn__piece');
  }

  async refreshStaleSection() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return false;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('_hp_section_refresh', Date.now().toString());
      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) return false;

      const html = await response.text();
      if (!html.includes('data-hp-button-label')) return false;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshSection = doc.getElementById(`shopify-section-${sectionId}`);
      const currentSection = this.closest('.shopify-section');
      if (freshSection && currentSection) {
        currentSection.replaceWith(document.importNode(freshSection, true));
        return true;
      }

      const freshCollection = doc.querySelector('hp-collection');
      if (freshCollection) {
        this.replaceWith(document.importNode(freshCollection, true));
        return true;
      }
    } catch (error) {
      console.error('Collection section refresh failed:', error);
    }

    return false;
  }

  disconnectedCallback() {
    const modalWasOpen =
      this.modal?.classList.contains('is-open')
      || this.buyingGuideModal?.classList.contains('is-open');
    document.removeEventListener('pointerdown', this.boundOutsidePointerdown);
    document.removeEventListener('keydown', this.boundKeydown);
    document.removeEventListener('touchstart', this.boundModalTouchStart, true);
    document.removeEventListener('touchmove', this.boundModalScrollBlock, true);
    document.removeEventListener('wheel', this.boundModalScrollBlock, true);
    document.removeEventListener('cart:update', this.boundCartStateUpdate);
    document.removeEventListener('cart:refresh', this.boundCartStateUpdate);
    window.removeEventListener('resize', this.boundPersistentScrollbarResize);
    window.removeEventListener('resize', this.boundSizePanelViewportChange);
    window.removeEventListener('resize', this.boundModalViewportChange);
    window.visualViewport?.removeEventListener('resize', this.boundModalViewportChange);
    window.removeEventListener('popstate', this.boundUrlPopState);
    if (this.modalViewportFrame) window.cancelAnimationFrame(this.modalViewportFrame);
    this.modalTrack?.removeEventListener('scroll', this.boundModalTrackScroll);
    this.modalSwipeSurfaces?.forEach((surface) => {
      surface?.removeEventListener('pointerdown', this.boundModalImageSwipeStart);
      surface?.removeEventListener('pointermove', this.boundModalImageSwipeMove);
      surface?.removeEventListener('pointerup', this.boundModalImageSwipeEnd);
      surface?.removeEventListener('pointercancel', this.boundModalImageSwipeCancel);
      surface?.removeEventListener('wheel', this.boundModalImageWheel);
      surface?.removeEventListener('dragstart', this.boundModalImageNativeDragStart);
    });
    window.removeEventListener('pageshow', this.boundPageShow);
    window.removeEventListener('load', this.boundStartProductDetailWarmup);
    this.cardImagePreloadObserver?.disconnect();
    this.productDetailPreloadObserver?.disconnect();
    this.teardownPersistentScrollbars();
    this.classList.remove('hp-modal-open', 'hp-buying-guide-open');
    document.body.classList.remove('hp-collection-modal-open', 'hp-collection-modal-header-transparent');
    this.querySelector('.hp-collection-page__inner')?.removeAttribute('inert');
    (document.querySelector('#header-group') || document.querySelector('.site-header'))?.removeAttribute('inert');
    if (modalWasOpen) this.unlockScroll();
    if (!document.querySelector('hp-collection')) document.body.classList.remove('hp-collection-active');
  }

  parseProducts(root = this) {
    const script = root?.querySelector?.('[data-hp-products-json]');
    if (!script) return [];

    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Collection products JSON could not be parsed:', error);
      return [];
    }
  }

  registerProducts(products) {
    const added = [];
    if (!Array.isArray(products)) return added;

    products.forEach((product) => {
      if (!product?.id) return;
      const productId = String(product.id);
      if (this.productMap.has(productId)) return;

      product.originalIndex = this.products.length;
      product.searchText = this.buildSearchText(product);
      product.searchTokens = this.buildSearchTokens(product.searchText);
      product.searchTokenSet = new Set(product.searchTokens);
      product.sizeFilters = this.getProductSizeFilters(product);
      this.products.push(product);
      this.productMap.set(productId, product);
      added.push(product);
    });

    return added;
  }

  getNextCollectionPageUrl(doc = document) {
    const href = doc?.querySelector?.('link[rel="next"]')?.getAttribute('href');
    if (!href) return '';

    try {
      return new URL(href, window.location.href).toString();
    } catch (error) {
      return '';
    }
  }

  loadRemainingCollectionPages() {
    if (this.collectionPageLoadRequest) return this.collectionPageLoadRequest;

    const firstNextUrl = this.getNextCollectionPageUrl();
    if (!firstNextUrl) return Promise.resolve(false);

    this.collectionPageLoadRequest = this.fetchRemainingCollectionPages(firstNextUrl)
      .finally(() => {
        this.collectionPageLoadRequest = null;
      });

    return this.collectionPageLoadRequest;
  }

  async fetchRemainingCollectionPages(firstNextUrl) {
    let nextUrl = firstNextUrl;
    let mergedAny = false;

    while (nextUrl && this.isConnected) {
      const normalizedUrl = new URL(nextUrl, window.location.href).toString();
      if (this.loadedCollectionPageUrls.has(normalizedUrl)) break;
      this.loadedCollectionPageUrls.add(normalizedUrl);

      let doc = null;
      try {
        const response = await fetch(normalizedUrl, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) break;

        const html = await response.text();
        doc = new DOMParser().parseFromString(html, 'text/html');
      } catch (error) {
        console.error('Collection page load failed:', error);
        break;
      }

      mergedAny = this.mergeCollectionPage(doc) || mergedAny;
      nextUrl = this.getNextCollectionPageUrl(doc);
    }

    if (mergedAny) this.refreshMergedCollectionState();
    return mergedAny;
  }

  mergeCollectionPage(doc) {
    const sourceCollection = doc?.querySelector?.('hp-collection');
    if (!sourceCollection || !this.grid) return false;

    const addedProducts = this.registerProducts(this.parseProducts(sourceCollection));
    const fragment = document.createDocumentFragment();
    let addedCards = 0;

    sourceCollection.querySelectorAll('[data-hp-card]').forEach((sourceCard) => {
      const productId = sourceCard.getAttribute('data-product-id');
      if (!productId || this.cardMap.has(String(productId))) return;

      const product = this.productMap.get(String(productId));
      if (!product) return;

      const card = document.importNode(sourceCard, true);
      card.style.setProperty('--hp-card-index', String(product.originalIndex || 0));
      fragment.append(card);
      addedCards += 1;
    });

    if (addedCards) this.grid.append(fragment);
    return addedProducts.length > 0 || addedCards > 0;
  }

  refreshMergedCollectionState() {
    const currentSizes = this.getSelectedSizes();

    this.cacheCards();
    this.populateSizeFilter();
    this.setSelectedSizes(currentSizes);
    this.setPricePlaceholders();
    this.applyFilters({ updateUrl: false });
    this.refreshCartQuantities({ updateButtons: true });
    if (this.pagination) this.pagination.hidden = true;
  }

  cacheElements() {
    this.grid = this.querySelector('[data-hp-grid]');
    this.controls = this.querySelector('.hp-collection-controls');
    this.searchInput = this.querySelector('[data-hp-search-filter]');
    this.searchClear = this.querySelector('[data-hp-search-clear]');
    this.sizeControl = this.querySelector('[data-hp-size-control]');
    this.sizeFilter = this.querySelector('[data-hp-size-filter]');
    this.sizeToggle = this.querySelector('[data-hp-size-toggle]');
    this.sizePanel = this.querySelector('[data-hp-size-panel]');
    this.sizeOptions = this.querySelector('[data-hp-size-options]');
    this.sizeAll = this.querySelector('[data-hp-size-all]');
    this.priceControl = this.querySelector('[data-hp-price-control]');
    this.priceToggle = this.querySelector('[data-hp-price-toggle]');
    this.pricePanel = this.querySelector('[data-hp-price-panel]');
    this.priceMinInput = this.querySelector('[data-hp-price-min]');
    this.priceMaxInput = this.querySelector('[data-hp-price-max]');
    this.sortSelect = this.querySelector('[data-hp-sort]');
    this.resetButton = this.querySelector('[data-hp-reset]');
    this.empty = this.querySelector('[data-hp-empty]');
    this.pagination = this.querySelector('[data-hp-pagination]');
    this.priceFloor = this.querySelector('[data-hp-price-floor]');
    this.visibleCount = this.querySelector('[data-hp-visible-count]');
    this.buyingGuideModal = this.querySelector('[data-hp-buying-guide]');
    this.buyingGuideClose = this.querySelector('.hp-buying-guide-modal__close');
    this.modal = this.querySelector('[data-hp-modal]');
    this.modalImage = this.querySelector('[data-hp-modal-image]');
    this.modalTitle = this.querySelector('[data-hp-modal-title]');
    this.modalVendor = this.querySelector('[data-hp-modal-vendor]');
    this.modalPrice = this.querySelector('[data-hp-modal-price]');
    this.modalDescription = this.querySelector('[data-hp-modal-description]');
    this.modalLink = this.querySelector('[data-hp-modal-link]');
    this.modalPrev = this.querySelector('[data-hp-modal-prev]');
    this.modalNext = this.querySelector('[data-hp-modal-next]');
    this.modalDots = this.querySelector('[data-hp-modal-dots]');
    this.persistentScrollAreas = Array.from(this.querySelectorAll('[data-hp-persistent-scroll-area]'));
    this.modalGallery = this.querySelector('.hp-product-modal__gallery');
    this.modalMedia = this.querySelector('.hp-product-modal__media');
    this.modalTrack = this.querySelector('[data-hp-modal-gallery]');
    this.modalSwipeSurfaces = [this.modalGallery, this.modalMedia].filter((element, index, elements) => (
      element instanceof HTMLElement && elements.indexOf(element) === index
    ));
  }

  setupPersistentScrollbars() {
    this.teardownPersistentScrollbars();
    this.persistentScrollAreas = Array.from(this.querySelectorAll('[data-hp-persistent-scroll-area]'));
    this.persistentScrollAreas.forEach((area) => {
      area.addEventListener('scroll', this.boundPersistentScrollbarScroll, { passive: true });
    });
    window.addEventListener('resize', this.boundPersistentScrollbarResize, { passive: true });

    if ('MutationObserver' in window) {
      this.persistentScrollbarMutationObserver = new MutationObserver(() => this.schedulePersistentScrollbarSync());
      this.persistentScrollAreas.forEach((area) => {
        this.persistentScrollbarMutationObserver.observe(area, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['hidden', 'open'],
        });
      });
    }

    if ('ResizeObserver' in window) {
      this.persistentScrollbarResizeObserver = new ResizeObserver(() => this.schedulePersistentScrollbarSync());
      this.persistentScrollAreas.forEach((area) => {
        this.persistentScrollbarResizeObserver.observe(area);
        const track = area.closest('[data-hp-persistent-scroll-shell]')?.querySelector('[data-hp-persistent-scrollbar]');
        if (track instanceof HTMLElement) this.persistentScrollbarResizeObserver.observe(track);
      });
    }

    this.schedulePersistentScrollbarSync();
  }

  teardownPersistentScrollbars() {
    this.persistentScrollAreas.forEach((area) => {
      area.removeEventListener('scroll', this.boundPersistentScrollbarScroll);
    });
    window.removeEventListener('resize', this.boundPersistentScrollbarResize);
    this.persistentScrollbarMutationObserver?.disconnect();
    this.persistentScrollbarResizeObserver?.disconnect();
    this.persistentScrollbarMutationObserver = null;
    this.persistentScrollbarResizeObserver = null;
    if (this.persistentScrollbarFrame) {
      window.cancelAnimationFrame(this.persistentScrollbarFrame);
      this.persistentScrollbarFrame = 0;
    }
  }

  schedulePersistentScrollbarSync() {
    if (this.persistentScrollbarFrame) return;
    this.persistentScrollbarFrame = window.requestAnimationFrame(() => {
      this.persistentScrollbarFrame = 0;
      this.persistentScrollAreas.forEach((area) => this.updatePersistentScrollbar(area));
    });
  }

  scheduleModalViewportSync() {
    if (
      !this.modal?.classList.contains('is-open')
      && !this.buyingGuideModal?.classList.contains('is-open')
    ) return;
    if (this.modalViewportFrame) return;

    this.modalViewportFrame = window.requestAnimationFrame(() => {
      this.modalViewportFrame = 0;
      if (this.modal?.classList.contains('is-open')) {
        this.resetModalTrackPosition(this.activeImageIndex);
        this.updateModalImageControls(this.modalImages, this.activeImageIndex);
      }
      this.schedulePersistentScrollbarSync();
    });
  }

  updatePersistentScrollbar(area) {
    if (!(area instanceof HTMLElement)) return;
    if (area.closest('.hp-product-modal[aria-hidden="true"], .hp-buying-guide-modal[aria-hidden="true"]')) return;
    const shell = area.closest('[data-hp-persistent-scroll-shell]');
    const track = shell?.querySelector('[data-hp-persistent-scrollbar]');
    const thumb = track?.querySelector('[data-hp-persistent-scroll-thumb]');
    if (!(track instanceof HTMLElement) || !(thumb instanceof HTMLElement)) return;

    const trackHeight = Math.max(0, track.clientHeight);
    const viewportHeight = Math.max(1, area.clientHeight);
    const contentHeight = Math.max(viewportHeight, area.scrollHeight);
    const scrollRange = Math.max(0, contentHeight - viewportHeight);
    const minimumThumbHeight = Math.min(30, trackHeight);
    const calculatedThumbHeight = scrollRange > 1
      ? Math.max(minimumThumbHeight, trackHeight * (viewportHeight / contentHeight))
      : trackHeight;
    const thumbHeight = Math.min(trackHeight, Math.max(0, calculatedThumbHeight));
    const thumbRange = Math.max(0, trackHeight - thumbHeight);
    const scrollProgress = scrollRange > 1
      ? Math.min(1, Math.max(0, area.scrollTop / scrollRange))
      : 0;
    const thumbOffset = Math.min(thumbRange, Math.max(0, thumbRange * scrollProgress));

    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translateY(${thumbOffset}px)`;
  }

  cacheCards() {
    this.cardMap.clear();
    this.querySelectorAll('[data-hp-card]').forEach((card) => {
      const productId = card.getAttribute('data-product-id');
      if (!productId) return;
      this.cardMap.set(String(productId), card);
      const product = this.productMap.get(String(productId));
      if (product) card.dataset.hpSizes = product.sizeFilters.map((size) => size.key).join('|');
      if (product) this.bindCardModalPrime(card, product);
      if (product) this.productDetailPreloadObserver?.observe(card);
    });
  }

  bindCardModalPrime(card, product) {
    if (!(card instanceof HTMLElement) || !product || card.dataset.hpModalPrimeBound === 'true') return;

    const quickButton = card.querySelector('[data-hp-open-product]');
    if (!(quickButton instanceof HTMLElement)) return;

    card.dataset.hpModalPrimeBound = 'true';
    const loadDetails = () => this.loadProductDetails(product);
    const prime = (priority = 'auto') => {
      loadDetails().then((loadedProduct) => {
        this.primeProductModalGallery(loadedProduct, priority, card);
      });
    };
    card.addEventListener('pointerenter', loadDetails, { passive: true });
    card.addEventListener('focusin', loadDetails);
    quickButton.addEventListener('pointerenter', () => prime('auto'), { passive: true });
    quickButton.addEventListener('pointerdown', () => prime('high'), { passive: true });
    quickButton.addEventListener('focus', () => prime('high'));
  }

  getProductDetailsUrl(product) {
    const root = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    return new URL(`${root}products/${encodeURIComponent(product.handle)}.js`, window.location.origin).toString();
  }

  loadProductDetails(product) {
    if (!product?.id || !product?.handle || product.detailsLoaded) return Promise.resolve(product);

    const productId = String(product.id);
    if (this.productDetailRequests.has(productId)) return this.productDetailRequests.get(productId);

    const request = fetch(this.getProductDetailsUrl(product), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product details request failed with ${response.status}`);
        return response.json();
      })
      .then((details) => {
        const mediaImages = Array.isArray(details?.media)
          ? details.media
            .filter((media) => media?.media_type === 'image' && media?.preview_image?.src)
            .map((media) => ({
              src: media.preview_image.src,
              previewSrc: this.getSizedShopifyImageSrc(media.preview_image.src, 520),
              alt: media.alt || details.title || product.title,
            }))
          : [];
        const fallbackImages = Array.isArray(details?.images)
          ? details.images.map((image) => {
            const src = typeof image === 'string' ? image : image?.src;
            if (!src) return null;
            return {
              src,
              previewSrc: this.getSizedShopifyImageSrc(src, 520),
              alt: image?.alt || details.title || product.title,
            };
          }).filter(Boolean)
          : [];

        product.images = mediaImages.length ? mediaImages : fallbackImages;
        product.descriptionHtml = details?.description || '';
        product.detailsLoaded = true;
        return product;
      })
      .catch((error) => {
        console.error('Product details could not be loaded:', error);
        return product;
      })
      .finally(() => {
        this.productDetailRequests.delete(productId);
      });

    this.productDetailRequests.set(productId, request);
    return request;
  }

  startProductDetailWarmup() {
    if (this.productDetailWarmupStarted || !this.isConnected || !('IntersectionObserver' in window)) return;

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection?.saveData || /^(?:slow-)?2g$/i.test(connection?.effectiveType || '')) return;

    this.productDetailWarmupStarted = true;
    this.productDetailPreloadObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        const productId = entry.target.getAttribute('data-product-id');
        const product = this.productMap.get(String(productId || ''));
        if (product) this.queueProductDetailPreload(product);
      });
    }, { rootMargin: '240px 0px' });

    this.cardMap.forEach((card) => this.productDetailPreloadObserver.observe(card));
  }

  queueProductDetailPreload(product) {
    if (this.backgroundProductDetailPreloadCount >= 8) return;
    if (!product || product.detailsLoaded || this.productDetailRequests.has(String(product.id))) return;
    if (this.productDetailPreloadQueue.some((queuedProduct) => queuedProduct === product)) return;

    this.backgroundProductDetailPreloadCount += 1;
    this.productDetailPreloadQueue.push(product);
    this.drainProductDetailPreloadQueue();
  }

  drainProductDetailPreloadQueue() {
    while (this.activeProductDetailPreloads < 2 && this.productDetailPreloadQueue.length) {
      const product = this.productDetailPreloadQueue.shift();
      this.activeProductDetailPreloads += 1;

      const run = () => {
        if (!this.isConnected) {
          this.activeProductDetailPreloads -= 1;
          return;
        }

        this.loadProductDetails(product).finally(() => {
          this.activeProductDetailPreloads -= 1;
          this.drainProductDetailPreloadQueue();
        });
      };

      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 1500 });
      else window.setTimeout(run, 600);
    }
  }

  bindEvents() {
    this.searchInput?.addEventListener('input', () => {
      this.updateSearchClearState();
      this.applyFilters();
    });
    this.searchInput?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.getSearchQuery()) return;
      event.preventDefault();
      this.clearSearchFilter();
    });
    this.searchClear?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.clearSearchFilter({ focus: true });
    });
    this.sizeFilter?.addEventListener('change', () => this.handleSizeFilterChange());
    this.sizeToggle?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.setSizePanelOpen(!this.isSizePanelOpen());
    });
    this.sizePanel?.addEventListener('change', (event) => this.handleDesktopSizePanelChange(event));
    this.sizePanel?.addEventListener('click', (event) => event.stopPropagation());
    this.sizePanel?.addEventListener('pointerdown', (event) => event.stopPropagation());
    this.sortSelect?.addEventListener('change', () => this.applyFilters());
    this.resetButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.resetFilters();
    });
    this.priceToggle?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.setPricePanelOpen(!this.isPricePanelOpen());
    });
    this.pricePanel?.addEventListener('click', (event) => event.stopPropagation());
    this.pricePanel?.addEventListener('pointerdown', (event) => event.stopPropagation());

    [this.priceMinInput, this.priceMaxInput].forEach((input) => {
      input?.addEventListener('input', () => {
        this.updatePriceToggleLabel();
        this.applyFilters();
      });
      input?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
      });
    });

    document.addEventListener('pointerdown', this.boundOutsidePointerdown, { passive: true });
    window.addEventListener('resize', this.boundSizePanelViewportChange, { passive: true });
    window.addEventListener('resize', this.boundModalViewportChange, { passive: true });
    window.visualViewport?.addEventListener('resize', this.boundModalViewportChange, { passive: true });
    this.modalTrack?.addEventListener('scroll', this.boundModalTrackScroll, { passive: true });
    this.modalSwipeSurfaces?.forEach((surface) => {
      surface.addEventListener('pointerdown', this.boundModalImageSwipeStart);
      surface.addEventListener('pointermove', this.boundModalImageSwipeMove, { passive: false });
      surface.addEventListener('pointerup', this.boundModalImageSwipeEnd);
      surface.addEventListener('pointercancel', this.boundModalImageSwipeCancel);
      surface.addEventListener('wheel', this.boundModalImageWheel, { passive: false });
      surface.addEventListener('dragstart', this.boundModalImageNativeDragStart);
    });
    [
      [this.modalPrev, -1],
      [this.modalNext, 1],
    ].forEach(([button, direction]) => {
      if (!(button instanceof HTMLElement)) return;

      const primeTarget = () => {
        const targetImage = this.modalImages[this.activeImageIndex + direction];
        if (!targetImage) return;
        this.preloadModalImage(targetImage.previewSrc, 'high');
        this.preloadModalImage(targetImage.src, 'high');
      };

      button.addEventListener('pointerenter', primeTarget, { passive: true });
      button.addEventListener('pointerdown', primeTarget, { passive: true });
      button.addEventListener('touchstart', primeTarget, { passive: true });
      button.addEventListener('focus', primeTarget);
    });
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const buyingGuideClose = target.closest('[data-hp-close-buying-guide]');
      if (buyingGuideClose) {
        event.preventDefault();
        event.stopPropagation();
        this.closeBuyingGuide({ restoreFocus: true });
        return;
      }

      const buyingGuideOpener = target.closest('[data-hp-open-buying-guide]');
      if (buyingGuideOpener) {
        event.preventDefault();
        this.openBuyingGuide(buyingGuideOpener, { focusClose: event.detail === 0 });
        return;
      }

      const mediaLink = target.closest('.hp-product-card__media');
      if (mediaLink?.dataset.hpGallerySwiped === 'true') {
        event.preventDefault();
        delete mediaLink.dataset.hpGallerySwiped;
        return;
      }

      const panelToggle = target.closest('[data-hp-modal-panel-toggle]');
      if (panelToggle) {
        event.preventDefault();
        this.setModalPanel(panelToggle.getAttribute('data-panel') || 'images');
        return;
      }

      const close = target.closest('[data-hp-close-modal]');
      if (close) {
        event.preventDefault();
        event.stopPropagation();
        this.closeModal({ restoreFocus: true });
        return;
      }

      const prev = target.closest('[data-hp-modal-prev]');
      if (prev) {
        event.preventDefault();
        this.showModalImage(this.activeImageIndex - 1);
        return;
      }

      const next = target.closest('[data-hp-modal-next]');
      if (next) {
        event.preventDefault();
        this.showModalImage(this.activeImageIndex + 1);
        return;
      }

      const dot = target.closest('[data-hp-modal-dot]');
      if (dot) {
        event.preventDefault();
        this.showModalImage(Number(dot.getAttribute('data-index') || 0));
        return;
      }

      const opener = target.closest('[data-hp-open-product]');
      if (opener) {
        const media = opener.closest('.hp-product-card__media');
        if (media?.dataset.hpGallerySwiped === 'true') {
          event.preventDefault();
          delete media.dataset.hpGallerySwiped;
          return;
        }
        event.preventDefault();
        const card = opener.closest('[data-hp-card]');
        this.openProduct(card?.getAttribute('data-product-id'), {
          card,
          opener,
          focusClose: event.detail === 0,
        });
        return;
      }

      const cardAdd = target.closest('[data-hp-card-add]');
      if (cardAdd) {
        event.preventDefault();
        const card = cardAdd.closest('[data-hp-card]');
        this.addCardToCart(card, cardAdd);
      }
    });

    this.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLSelectElement)) return;
      if (target.matches('[data-hp-sort]')) {
        this.applyFilters();
        return;
      }
      if (target.matches('[data-hp-card-variant]')) this.syncCardVariant(target);
    });
  }

  buildSearchText(product) {
    return [
      product.title,
      product.vendor,
      product.type,
      ...(Array.isArray(product.tags) ? product.tags : []),
      ...(Array.isArray(product.sizes) ? product.sizes : []),
      ...(Array.isArray(product.variants) ? product.variants.flatMap((variant) => [variant.title, ...(variant.options || [])]) : []),
    ].filter(Boolean).join(' ').toLowerCase();
  }

  buildSearchTokens(value) {
    return Array.from(new Set(String(value || '').toLowerCase().match(/[a-z0-9]+(?:\.[a-z0-9]+)?/g) || []));
  }

  normalizeSizeLabel(value) {
    return this.extractSizeLabels(value)[0] || '';
  }

  parseSizeAliasRules(value) {
    return String(value || '')
      .split(/\r?\n|;/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [source, target] = line.split(/\s*(?:=>|=|:|,)\s*/, 2);
        const label = this.normalizeAliasTargetLabel(target);
        return source && label ? { source: source.trim().toLowerCase(), label } : null;
      })
      .filter(Boolean);
  }

  normalizeAliasTargetLabel(value) {
    const match = String(value || '').match(/(?:^|[^0-9.])((?:[6-9]|10)(?:\.\d{1,4})?)/i);
    if (!match) return '';

    const parsed = Number.parseFloat(match[1]);
    if (!Number.isFinite(parsed) || parsed < 6 || parsed > 10.5) return '';

    const label = parsed.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
    return label.includes('.') ? label : `${label}.0`;
  }

  getSizeAliasLabelsForText(text) {
    if (!this.sizeAliasRules.length) return [];
    return this.sizeAliasRules
      .filter((rule) => this.matchesSizeAlias(text, rule.source))
      .map((rule) => rule.label);
  }

  matchesSizeAlias(text, source) {
    if (!source) return false;
    const escaped = source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (/^\d+(?:\.\d+)?$/.test(source)) {
      return new RegExp(`(^|[^0-9a-z])${escaped}\\s*(?:mm)?(?=$|[^0-9a-z])`, 'i').test(text);
    }
    return new RegExp(`(^|[^0-9a-z])${escaped}(?=$|[^0-9a-z])`, 'i').test(text);
  }

  extractSizeLabels(value) {
    const text = String(value || '').trim();
    if (!text) return [];

    const lowered = text.toLowerCase();
    if (/\b(?:skate\s*tool|tool|grip\s*tape|griptape|no\s+tool|with\s+tool|without\s+tool|add\s+tool)\b/.test(lowered)) return [];
    if (/\b(?:abec\s*-?\s*\d+|bearings?)\b/.test(lowered)) return [];

    const labels = [];
    const addLabel = (label) => {
      if (!label) return;
      const key = label.toLowerCase();
      if (!labels.some((entry) => entry.toLowerCase() === key)) labels.push(label);
    };

    const wheel = text.match(/\b(\d{2,3})\s*mm\b/i);
    if (wheel) {
      const wheelSize = Number(wheel[1]);
      if (Number.isFinite(wheelSize) && wheelSize >= 20 && wheelSize <= 130) addLabel(`${wheelSize}mm`);
    }

    for (const hardness of text.matchAll(/\b((?:[5-9]\d|10\d|110))([ab])\b/gi)) {
      addLabel(`${Number(hardness[1])}${hardness[2].toUpperCase()}`);
    }

    this.getSizeAliasLabelsForText(text).forEach(addLabel);

    const explicitDimension = text.match(/(?:^|[^0-9a-z.])((?:[6-9]|10)(?:\.\d{1,4})?)(?=\s*(?:"|\u201c|\u201d|\u2033|in\b|inch(?:es)?\b|x\b|\/|,|&|\+|\bto\b|$))/i);
    const decimalDimension = text.match(/(?:^|[^0-9a-z.])((?:[6-9]|10)\.\d{1,4})(?=\s*(?:[a-z]|$))/i);
    const dimension = explicitDimension || decimalDimension;
    if (!dimension) return labels;

    const parsed = Number.parseFloat(dimension[1]);
    if (!Number.isFinite(parsed) || parsed < 6 || parsed > 10.5) return labels;

    const label = parsed.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
    addLabel(label.includes('.') ? label : `${label}.0`);
    return labels;
  }

  getProductSizeFilters(product) {
    const sizes = new Map();
    const add = (raw) => {
      this.extractSizeLabels(raw).forEach((label) => {
        const key = label.toLowerCase();
        if (!sizes.has(key)) sizes.set(key, { key, label, sort: this.getSizeSortValue(label) });
      });
    };

    (product.sizes || []).forEach(add);
    (product.variants || []).forEach((variant) => {
      add(variant.title);
      (variant.options || []).forEach(add);
    });
    add(product.title);

    return Array.from(sizes.values());
  }

  getSizeSortValue(label) {
    const number = Number.parseFloat(String(label).match(/\d+(?:\.\d+)?/)?.[0] || '9999');
    return Number.isFinite(number) ? number : 9999;
  }

  populateSizeFilter() {
    this.updateSizeFilterOptions();
  }

  getProductsForSizeOptions({ query = this.getSearchQuery(), price = this.getActivePriceRange() } = {}) {
    return this.products.filter((product) => (
      this.matchesSearch(product, query) && this.matchesPrice(product, price, [])
    ));
  }

  updateSizeFilterOptions({ query = this.getSearchQuery(), price = this.getActivePriceRange() } = {}) {
    if (!this.sizeFilter) return [];
    const selectedSizes = new Set(this.getSelectedSizes());
    const sizes = new Map();
    this.getProductsForSizeOptions({ query, price }).forEach((product) => product.sizeFilters.forEach((size) => {
      if (!sizes.has(size.key)) sizes.set(size.key, size);
    }));
    const sorted = Array.from(sizes.values()).sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label));
    const availableSizeKeys = new Set(sorted.map((size) => size.key));
    const nextSelectedSizes = Array.from(selectedSizes).filter((size) => availableSizeKeys.has(size));
    const optionSignature = sorted.map((size) => `${size.key}:${size.label}`).join('|');
    const shouldRebuildOptions = this.sizeFilter.dataset.hpSizeOptionSignature !== optionSignature;

    if (shouldRebuildOptions) {
      this.sizeFilter.innerHTML = [
        '<option value="all">All sizes</option>',
        ...sorted.map((size) => `<option value="${this.escapeHtml(size.key)}">${this.escapeHtml(size.label)}</option>`)
      ].join('');
      this.sizeFilter.dataset.hpSizeOptionSignature = optionSignature;
      if (this.sizeOptions) {
        this.sizeOptions.innerHTML = sorted.map((size) => (
          `<label class="hp-collection-size-option">
            <input type="checkbox" value="${this.escapeHtml(size.key)}" data-hp-size-option>
            <span>${this.escapeHtml(size.label)}</span>
          </label>`
        )).join('');
      }
    }

    this.setSelectedSizes(nextSelectedSizes);
    if (shouldRebuildOptions && this.isSizePanelOpen()) {
      this.resetSizePanelScroll();
      this.updateSizePanelPosition();
    }

    return nextSelectedSizes;
  }

  handleSizeFilterChange() {
    if (!this.sizeFilter) return;
    const selectedOptions = Array.from(this.sizeFilter.selectedOptions || []);
    const hasAll = selectedOptions.some((option) => option.value === 'all');
    const selectedSizes = selectedOptions
      .map((option) => String(option.value || '').trim().toLowerCase())
      .filter((value) => value && value !== 'all');
    const previousSizes = Array.isArray(this.lastSelectedSizes) ? this.lastSelectedSizes : [];
    const isSameAsPrevious = selectedSizes.length === previousSizes.length
      && selectedSizes.every((size) => previousSizes.includes(size));
    const nextSizes = hasAll && (!selectedSizes.length || isSameAsPrevious) ? [] : selectedSizes;
    this.setSelectedSizes(nextSizes);
    this.applyFilters();
  }

  handleDesktopSizePanelChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-hp-size-all]')) {
      if (target.checked) {
        this.setSelectedSizes([]);
        this.applyFilters();
      } else if (!this.getSelectedSizes().length) {
        target.checked = true;
      }
      return;
    }

    if (target.matches('[data-hp-size-option]')) {
      const selectedSizes = Array.from(this.sizeOptions?.querySelectorAll('[data-hp-size-option]:checked') || [])
        .map((input) => String(input.value || '').trim().toLowerCase())
        .filter(Boolean);
      this.setSelectedSizes(selectedSizes);
      this.applyFilters();
    }
  }

  getSelectedSizes() {
    if (!this.sizeFilter) return [];
    return Array.from(this.sizeFilter.selectedOptions || [])
      .map((option) => String(option.value || '').trim().toLowerCase())
      .filter((value) => value && value !== 'all')
      .filter(Boolean);
  }

  setSelectedSizes(sizes) {
    const selectedSizes = new Set((Array.isArray(sizes) ? sizes : [])
      .map((size) => String(size || '').trim().toLowerCase())
      .filter(Boolean));
    Array.from(this.sizeFilter?.options || []).forEach((option) => {
      const value = String(option.value || '').trim().toLowerCase();
      option.selected = selectedSizes.size ? selectedSizes.has(value) : value === 'all';
    });
    this.sizeOptions?.querySelectorAll('[data-hp-size-option]').forEach((input) => {
      input.checked = selectedSizes.has(String(input.value || '').trim().toLowerCase());
    });
    if (this.sizeAll) this.sizeAll.checked = selectedSizes.size === 0;
    this.updateSizeToggleLabel(selectedSizes);
    this.lastSelectedSizes = Array.from(selectedSizes);
  }

  updateSizeToggleLabel(selectedSizes = new Set(this.getSelectedSizes())) {
    if (!this.sizeToggle) return;
    const selected = Array.from(selectedSizes);
    if (!selected.length) {
      this.sizeToggle.textContent = 'All sizes';
      return;
    }
    if (selected.length === 1) {
      const option = Array.from(this.sizeFilter?.options || []).find((entry) => entry.value === selected[0]);
      this.sizeToggle.textContent = option?.textContent?.trim() || selected[0];
      return;
    }
    this.sizeToggle.textContent = `${selected.length} sizes`;
  }

  applyFilters({ updateUrl = true } = {}) {
    const query = this.getSearchQuery();
    const price = this.getActivePriceRange();
    const sizes = this.updateSizeFilterOptions({ query, price });
    const sort = this.sortSelect?.value || 'manual';
    const visibleProducts = [];

    this.products.forEach((product) => {
      const matches = this.matchesSearch(product, query) && this.matchesSize(product, sizes) && this.matchesPrice(product, price, sizes);
      const card = this.cardMap.get(String(product.id));
      card?.classList.toggle('is-filtered-out', !matches);
      card?.setAttribute('aria-hidden', matches ? 'false' : 'true');
      if (matches) visibleProducts.push(product);
    });

    this.sortProducts(visibleProducts, sort, sizes).forEach((product) => {
      const card = this.cardMap.get(String(product.id));
      if (card) this.grid?.append(card);
    });

    if (this.empty) {
      const isEmpty = visibleProducts.length === 0;
      this.empty.hidden = !isEmpty;
      this.empty.textContent = isEmpty
        ? (this.hasAttribute('data-hp-search-page') ? 'No search results match these filters.' : 'No products match these filters.')
        : '';
    }
    if (this.visibleCount) this.visibleCount.textContent = `${visibleProducts.length} ${visibleProducts.length === 1 ? 'item' : 'items'}`;
    this.updatePriceFloor(visibleProducts, sizes);
    this.updateSearchClearState();
    this.resetButton?.classList.toggle('is-active-filter', this.hasActiveFilters());
    if (updateUrl) this.updateUrlState();
  }

  applyStateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const search = params.get('hp_search');
    const sizes = params.getAll('hp_size')
      .flatMap((value) => value.split(','))
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
    const sort = params.get('hp_sort');
    const priceMin = params.get('hp_price_min');
    const priceMax = params.get('hp_price_max');

    if (this.searchInput) this.searchInput.value = search || '';
    this.setSelectedSizes(sizes);
    if (this.sortSelect) this.sortSelect.value = sort && this.hasSelectOption(this.sortSelect, sort) ? sort : 'manual';
    if (this.priceMinInput) this.priceMinInput.value = priceMin !== null ? this.sanitizeUrlPriceValue(priceMin) : '';
    if (this.priceMaxInput) this.priceMaxInput.value = priceMax !== null ? this.sanitizeUrlPriceValue(priceMax) : '';
    this.updatePriceToggleLabel();
    this.updateSearchClearState();
  }

  updateUrlState() {
    if (!window.history?.replaceState) return;

    const url = new URL(window.location.href);
    const search = String(this.searchInput?.value || '').trim();
    const sizes = this.getSelectedSizes();
    const sort = this.sortSelect?.value || 'manual';
    const priceMin = String(this.priceMinInput?.value || '').trim();
    const priceMax = String(this.priceMaxInput?.value || '').trim();

    this.setUrlParam(url, 'hp_search', search);
    url.searchParams.delete('hp_size');
    sizes.forEach((size) => url.searchParams.append('hp_size', size));
    this.setUrlParam(url, 'hp_sort', sort === 'manual' ? '' : sort);
    this.setUrlParam(url, 'hp_price_min', this.sanitizeUrlPriceValue(priceMin));
    this.setUrlParam(url, 'hp_price_max', this.sanitizeUrlPriceValue(priceMax));

    window.history.replaceState(window.history.state, '', url.toString());
  }

  setUrlParam(url, key, value) {
    const nextValue = String(value || '').trim();
    if (nextValue) url.searchParams.set(key, nextValue);
    else url.searchParams.delete(key);
  }

  hasSelectOption(select, value) {
    return Array.from(select.options).some((option) => option.value === value);
  }

  sanitizeUrlPriceValue(value) {
    const cleaned = String(value || '').replace(/[^0-9.]/g, '');
    if (!cleaned) return '';
    const amount = Number.parseFloat(cleaned);
    if (!Number.isFinite(amount) || amount < 0) return '';
    return cleaned;
  }

  getSearchQuery() {
    return String(this.searchInput?.value || '').trim().toLowerCase();
  }

  matchesSearch(product, query) {
    if (!query) return true;
    const terms = this.buildSearchTokens(query);
    const tokens = Array.isArray(product.searchTokens) ? product.searchTokens : this.buildSearchTokens(product.searchText);
    const tokenSet = product.searchTokenSet instanceof Set ? product.searchTokenSet : new Set(tokens);
    if (!terms.length) return true;

    return terms.every((term) => (
      tokenSet.has(term) || tokens.some((token) => token.startsWith(term))
    ));
  }

  clearSearchFilter({ focus = false } = {}) {
    if (!this.searchInput) return;
    this.searchInput.value = '';
    this.updateSearchClearState();
    this.applyFilters();
    if (focus) this.searchInput.focus({ preventScroll: true });
  }

  updateSearchClearState() {
    if (!this.searchClear) return;
    this.searchClear.hidden = !this.getSearchQuery();
  }

  matchesSize(product, sizes) {
    if (!Array.isArray(sizes) || !sizes.length) return true;
    return product.sizeFilters.some((entry) => sizes.includes(entry.key));
  }

  matchesPrice(product, range, sizes = []) {
    if (!range || (!Number.isFinite(range.min) && !Number.isFinite(range.max))) return true;
    const price = this.getProductFilterPrice(product, sizes);
    const min = Number.isFinite(range.min) ? range.min : 0;
    const max = Number.isFinite(range.max) ? range.max : Number.POSITIVE_INFINITY;
    return price >= min && price <= max;
  }

  hasActiveFilters() {
    const hasSearch = Boolean(this.getSearchQuery());
    const hasSize = this.getSelectedSizes().length > 0;
    const hasSort = this.sortSelect && this.sortSelect.value !== 'manual';
    const hasPrice = Boolean(String(this.priceMinInput?.value || '').trim() || String(this.priceMaxInput?.value || '').trim());
    return Boolean(hasSearch || hasSize || hasSort || hasPrice);
  }

  sortProducts(products, sort, sizes = []) {
    const sorted = [...products];
    if (sort === 'price-asc') sorted.sort((a, b) => this.getProductFilterPrice(a, sizes) - this.getProductFilterPrice(b, sizes));
    else if (sort === 'price-desc') sorted.sort((a, b) => this.getProductFilterPrice(b, sizes) - this.getProductFilterPrice(a, sizes));
    else sorted.sort((a, b) => a.originalIndex - b.originalIndex);
    return sorted;
  }

  getProductFilterPrice(product, sizes = []) {
    const fallback = Number(product?.priceMin || product?.priceMax || 0);
    if (!Array.isArray(sizes) || !sizes.length) return Number.isFinite(fallback) ? fallback : 0;

    const matchingPrices = (product?.variants || []).reduce((prices, variant) => {
      const candidates = [variant.title, ...(variant.options || [])];
      const hasSize = candidates.some((value) => this.extractSizeLabels(value).some((label) => sizes.includes(label.toLowerCase())));
      const price = Number(variant.price || 0);
      if (hasSize && Number.isFinite(price) && price > 0) prices.push(price);
      return prices;
    }, []);

    if (matchingPrices.length) return Math.min(...matchingPrices);
    return Number.isFinite(fallback) ? fallback : 0;
  }

  updatePriceFloor(products, sizes = []) {
    if (!this.priceFloor) return;
    const prices = products
      .map((product) => this.getProductFilterPrice(product, sizes))
      .filter((price) => Number.isFinite(price) && price > 0);

    if (!prices.length) {
      this.priceFloor.hidden = true;
      return;
    }

    this.priceFloor.hidden = false;
    this.priceFloor.textContent = `From ${this.formatMoney(Math.min(...prices))}`;
  }

  resetFilters() {
    const keepPanelsOpen = this.shouldKeepFilterPanelsOpenUntilToggle();
    if (this.searchInput) this.searchInput.value = '';
    this.setSelectedSizes([]);
    if (this.sortSelect) this.sortSelect.value = 'manual';
    this.updateSearchClearState();
    this.clearPriceRange({ apply: false, close: !keepPanelsOpen });
    this.resetButton?.classList.remove('is-active-filter');
    this.resetButton?.classList.add('is-pressed');
    this.resetButton?.blur?.();
    window.setTimeout(() => {
      this.resetButton?.classList.remove('is-pressed');
      this.resetButton?.blur?.();
    }, 180);
    this.applyFilters();
  }

  normalizePriceInput(value) {
    const amount = Number.parseFloat(String(value || '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(amount) ? Math.round(amount * 100) : Number.NaN;
  }

  getActivePriceRange() {
    return {
      min: this.normalizePriceInput(this.priceMinInput?.value),
      max: this.normalizePriceInput(this.priceMaxInput?.value),
    };
  }

  updatePriceToggleLabel() {
    if (!this.priceToggle) return;
    const minRaw = String(this.priceMinInput?.value || '').trim();
    const maxRaw = String(this.priceMaxInput?.value || '').trim();
    const minLabel = this.formatPriceInputLabel(minRaw);
    const maxLabel = this.formatPriceInputLabel(maxRaw);
    if (minLabel && maxLabel) this.priceToggle.textContent = `From ${minLabel}-${maxLabel}`;
    else if (minLabel) this.priceToggle.textContent = `From ${minLabel}`;
    else if (maxLabel) this.priceToggle.textContent = `Under ${maxLabel}`;
    else this.priceToggle.textContent = 'Price';
  }

  isPricePanelOpen() {
    return Boolean(this.priceControl?.classList.contains('is-open') || (this.pricePanel && !this.pricePanel.hidden));
  }

  setPricePlaceholders() {
    if (this.priceMinInput) this.priceMinInput.placeholder = '0';
    if (!this.priceMaxInput) return;

    const highestPrice = this.products.reduce((highest, product) => {
      const price = Number(product?.priceMax || product?.priceMin || 0);
      return Number.isFinite(price) && price > highest ? price : highest;
    }, 0);
    if (!highestPrice) return;

    const amount = highestPrice / 100;
    const formatted = Number.isInteger(amount) ? String(amount) : amount.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    this.priceMaxInput.placeholder = formatted;
  }

  setPricePanelOpen(isOpen) {
    if (isOpen) this.setSizePanelOpen(false);
    this.priceControl?.classList.toggle('is-open', isOpen);
    this.controls?.classList.toggle('has-price-open', isOpen);
    if (this.pricePanel) this.pricePanel.hidden = !isOpen;
    this.priceToggle?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  isSizePanelOpen() {
    return Boolean(this.sizeControl?.classList.contains('is-open') || (this.sizePanel && !this.sizePanel.hidden));
  }

  setSizePanelOpen(isOpen) {
    if (isOpen) this.setPricePanelOpen(false);
    this.sizeControl?.classList.toggle('is-open', isOpen);
    this.controls?.classList.toggle('has-size-open', isOpen);
    if (this.sizePanel) this.sizePanel.hidden = !isOpen;
    this.sizeToggle?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    if (isOpen) {
      this.resetSizePanelScroll();
      this.updateSizePanelPosition();
    } else {
      this.sizeControl?.classList.remove('is-drop-up', 'is-drop-down');
      this.sizePanel?.style.removeProperty('--hp-size-panel-max-height');
    }
  }

  resetSizePanelScroll() {
    [this.sizePanel, this.sizeOptions].forEach((element) => {
      if (element instanceof HTMLElement) element.scrollTop = 0;
    });
  }

  updateSizePanelPosition() {
    if (!this.sizeControl || !this.sizePanel || this.sizePanel.hidden) return;
    const rect = this.sizeControl.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const gap = 12;
    const availableBelow = Math.max(0, viewportHeight - rect.bottom - gap);
    const availableAbove = Math.max(0, rect.top - gap);
    const naturalHeight = Math.max(180, Math.min(this.sizePanel.scrollHeight || 0, 420));
    const openDown = availableBelow >= Math.min(naturalHeight, 260) || availableBelow >= availableAbove;
    const availableSpace = openDown ? availableBelow : availableAbove;
    const maxHeight = Math.max(160, Math.min(naturalHeight, availableSpace || naturalHeight));

    this.sizeControl.classList.toggle('is-drop-down', openDown);
    this.sizeControl.classList.toggle('is-drop-up', !openDown);
    this.sizePanel.style.setProperty('--hp-size-panel-max-height', `${Math.round(maxHeight)}px`);
  }

  clearPriceRange({ apply = true, close = false } = {}) {
    if (this.priceMinInput) this.priceMinInput.value = '';
    if (this.priceMaxInput) this.priceMaxInput.value = '';
    this.updatePriceToggleLabel();
    if (close) this.setPricePanelOpen(false);
    if (apply) this.applyFilters();
  }

  handleOutsidePointerdown(event) {
    if (!this.isPricePanelOpen() && !this.isSizePanelOpen()) return;
    if (this.shouldKeepFilterPanelsOpenUntilToggle()) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target && (
      target.closest('[data-hp-price-control]')
      || target.closest('[data-hp-price-panel]')
      || target.closest('[data-hp-size-control]')
      || target.closest('[data-hp-size-panel]')
    )) return;
    this.setPricePanelOpen(false);
    this.setSizePanelOpen(false);
  }

  shouldKeepFilterPanelsOpenUntilToggle() {
    return window.matchMedia('(max-width: 1180px)').matches;
  }

  enhanceCardGalleries() {
    this.cardMap.forEach((card, productId) => {
      const product = this.productMap.get(String(productId));
      const images = this.getProductImages(product).slice(0, 6);
      const media = card.querySelector('.hp-product-card__media');
      if (!(media instanceof HTMLElement) || images.length <= 1 || media.dataset.hpGalleryReady === 'true') return;

      const cardIndex = Number.parseInt(card.style.getPropertyValue('--hp-card-index') || '0', 10) || 0;
      media.dataset.hpGalleryReady = 'true';
      media.innerHTML = `
        <span class="hp-product-card__media-track" data-hp-card-gallery>
          ${images.map((image, index) => {
            const isPriority = index === 0 && cardIndex < 8;
            return `<span class="hp-product-card__media-slide"><img src="${this.escapeHtml(image.previewSrc || image.src || '')}" alt="${this.escapeHtml(image.alt || product.title || '')}" width="720" height="720" loading="${isPriority ? 'eager' : 'lazy'}" fetchpriority="${isPriority ? 'high' : 'auto'}" decoding="async" data-index="${index}"></span>`;
          }).join('')}
        </span>
        <span class="hp-product-card__media-dots" aria-hidden="true">
          ${images.map((_, index) => `<span class="hp-product-card__media-dot${index === 0 ? ' is-active' : ''}"></span>`).join('')}
        </span>
      `;
      if (cardIndex < 8) this.preloadCardGalleryImages(media);
      else this.getCardImagePreloadObserver()?.observe(media);
      this.bindCardGallery(media);
    });
  }

  getCardImagePreloadObserver() {
    if (!('IntersectionObserver' in window)) return null;

    this.cardImagePreloadObserver ??= new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          this.preloadCardGalleryImages(entry.target);
        });
      },
      { rootMargin: '1200px 0px' }
    );

    return this.cardImagePreloadObserver;
  }

  preloadCardGalleryImages(media) {
    if (!(media instanceof HTMLElement)) return;

    media.querySelectorAll('.hp-product-card__media-track img').forEach((image, index) => {
      if (!(image instanceof HTMLImageElement) || index > 1) return;

      image.loading = 'eager';
      image.decoding = 'async';
      image.setAttribute('fetchpriority', index === 0 ? 'high' : 'auto');
      if (typeof image.decode === 'function') image.decode().catch(() => {});
    });

    const card = media.closest('[data-hp-card]');
    const productId = card?.getAttribute('data-product-id');
    const product = this.productMap.get(String(productId || ''));
    if (product) this.primeProductModalGallery(product, 'auto', card);
  }

  bindCardGallery(media) {
    const track = media.querySelector('[data-hp-card-gallery]');
    const dots = Array.from(media.querySelectorAll('.hp-product-card__media-dot'));
    if (!(track instanceof HTMLElement)) return;

    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let pointerId = null;
    let isDragging = false;
    let swiped = false;
    const isSwipeLayout = () => window.matchMedia('(max-width: 989px)').matches;
    const updateDots = () => {
      const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
    };

    track.addEventListener('scroll', () => window.requestAnimationFrame(updateDots), { passive: true });
    track.addEventListener('pointerdown', (event) => {
      if (!isSwipeLayout()) return;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = track.scrollLeft;
      pointerId = event.pointerId;
      isDragging = true;
      swiped = false;
    });
    track.addEventListener('pointermove', (event) => {
      if (!isDragging || event.pointerId !== pointerId) return;
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      if (Math.abs(deltaY) > 8 && Math.abs(deltaY) > Math.abs(deltaX)) {
        isDragging = false;
        pointerId = null;
        return;
      }
      if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
        swiped = true;
        media.dataset.hpGallerySwiped = 'true';
        track.setPointerCapture?.(event.pointerId);
        track.scrollLeft = startScrollLeft - deltaX;
        event.preventDefault();
      }
    });
    const finishSwipe = (event) => {
      if (event?.pointerId !== pointerId) return;
      isDragging = false;
      track.releasePointerCapture?.(pointerId);
      pointerId = null;
      if (!swiped) return;
      window.setTimeout(() => {
        delete media.dataset.hpGallerySwiped;
      }, 240);
    };
    track.addEventListener('pointerup', finishSwipe);
    track.addEventListener('pointercancel', finishSwipe);
  }

  prepareModalHeaderState() {
    const header = document.querySelector('.site-header');
    document.body.classList.toggle(
      'hp-collection-modal-header-transparent',
      header instanceof HTMLElement && !header.classList.contains('is-scrolled')
    );
  }

  clearModalHeaderState() {
    document.body.classList.remove('hp-collection-modal-header-transparent');
  }

  syncModalBackgroundInert() {
    const modalOpen = Boolean(
      this.modal?.classList.contains('is-open')
      || this.buyingGuideModal?.classList.contains('is-open')
    );
    this.querySelector('.hp-collection-page__inner')?.toggleAttribute('inert', modalOpen);
    (document.querySelector('#header-group') || document.querySelector('.site-header'))?.toggleAttribute('inert', modalOpen);
  }

  resetModalState() {
    if (!this.modal) return;
    const wasOpen = this.modal.classList.contains('is-open') || this.modal.getAttribute('aria-hidden') === 'false' || this.classList.contains('hp-modal-open');
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    this.modal.setAttribute('inert', '');
    const closesAllModals = !this.buyingGuideModal?.classList.contains('is-open');
    if (closesAllModals) {
      this.classList.remove('hp-modal-open');
      document.body.classList.remove('hp-collection-modal-open');
      document.removeEventListener('keydown', this.boundKeydown);
    }
    this.syncModalBackgroundInert();
    if (closesAllModals) this.clearModalHeaderState();
    this.activeProduct = null;
    this.activeImageIndex = 0;
    if (this.modalTrackScrollFrame) {
      window.cancelAnimationFrame(this.modalTrackScrollFrame);
      this.modalTrackScrollFrame = 0;
    }
    if (wasOpen && closesAllModals) this.unlockScroll();
  }

  resetBuyingGuideState() {
    if (!this.buyingGuideModal) return;
    const wasOpen =
      this.buyingGuideModal.classList.contains('is-open')
      || this.buyingGuideModal.getAttribute('aria-hidden') === 'false'
      || this.classList.contains('hp-buying-guide-open');
    this.buyingGuideModal.classList.remove('is-open');
    this.buyingGuideModal.setAttribute('aria-hidden', 'true');
    this.buyingGuideModal.setAttribute('inert', '');
    this.classList.remove('hp-buying-guide-open');
    this.syncModalBackgroundInert();
    const closesAllModals = !this.modal?.classList.contains('is-open');
    if (closesAllModals) {
      this.classList.remove('hp-modal-open');
      document.body.classList.remove('hp-collection-modal-open');
      this.clearModalHeaderState();
      document.removeEventListener('keydown', this.boundKeydown);
    }
    this.buyingGuideLastFocusedElement = null;
    if (wasOpen && closesAllModals) this.unlockScroll();
  }

  openBuyingGuide(opener, { focusClose = false } = {}) {
    if (!this.buyingGuideModal) return;
    if (this.modal?.classList.contains('is-open')) this.resetModalState();

    this.buyingGuideLastFocusedElement =
      opener instanceof HTMLElement
        ? opener
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

    this.prepareModalHeaderState();
    this.classList.add('hp-modal-open', 'hp-buying-guide-open');
    document.body.classList.add('hp-collection-modal-open');
    this.lockScroll();
    this.buyingGuideModal.removeAttribute('inert');
    this.buyingGuideModal.classList.add('is-open');
    this.buyingGuideModal.setAttribute('aria-hidden', 'false');
    this.syncModalBackgroundInert();
    document.addEventListener('keydown', this.boundKeydown);
    window.requestAnimationFrame(() => {
      const body = this.buyingGuideModal?.querySelector('.hp-buying-guide-modal__body');
      const content = this.buyingGuideModal?.querySelector('.hp-buying-guide-modal__content');
      this.buyingGuideModal?.querySelectorAll('.hp-buying-guide-modal__faq[open]').forEach((faq) => {
        faq.removeAttribute('open');
      });
      if (body instanceof HTMLElement) body.scrollTop = 0;
      content?.classList.remove('is-height-locked');
      content?.style.removeProperty('--hp-guide-locked-height');
      const focusTarget = focusClose ? this.buyingGuideClose : content;
      focusTarget?.focus?.({ preventScroll: true });
      this.schedulePersistentScrollbarSync();
    });
  }

  closeBuyingGuide({ restoreFocus = true } = {}) {
    if (!this.buyingGuideModal) return;
    const wasOpen =
      this.buyingGuideModal.classList.contains('is-open')
      || this.buyingGuideModal.getAttribute('aria-hidden') === 'false';
    const focusTarget = this.buyingGuideLastFocusedElement;

    this.buyingGuideModal.classList.remove('is-open');
    this.buyingGuideModal.setAttribute('aria-hidden', 'true');
    this.buyingGuideModal.setAttribute('inert', '');
    this.classList.remove('hp-buying-guide-open');
    this.syncModalBackgroundInert();
    const closesAllModals = !this.modal?.classList.contains('is-open');
    if (closesAllModals) {
      this.classList.remove('hp-modal-open');
      document.removeEventListener('keydown', this.boundKeydown);
    }
    this.buyingGuideLastFocusedElement = null;

    window.requestAnimationFrame(() => {
      if (wasOpen && closesAllModals) this.unlockScroll();
      if (closesAllModals) {
        document.body.classList.remove('hp-collection-modal-open');
        this.clearModalHeaderState();
      }
      if (wasOpen && restoreFocus) focusTarget?.focus?.({ preventScroll: true });
    });
  }

  syncCardVariant(select) {
    const option = select.selectedOptions?.[0];
    const card = select.closest('[data-hp-card]');
    const priceEl = card?.querySelector('[data-hp-card-price]');
    if (priceEl && option?.dataset.price) {
      const price = Number(option.dataset.price);
      priceEl.innerHTML = this.formatProductPrice({ priceMin: price, priceMax: price });
    }
  }

  openProduct(productId, options = {}) {
    const product = this.productMap.get(String(productId || ''));
    if (!product || !this.modal) return;
    if (this.buyingGuideModal?.classList.contains('is-open')) this.resetBuyingGuideState();

    const detailsAlreadyLoaded = product.detailsLoaded === true;
    this.activeProduct = product;
    this.activeImageIndex = 0;
    this.activeModalCardImageSrcs = this.getCardGalleryImageSources(options.card);
    this.lastFocusedElement =
      options.opener instanceof HTMLElement
        ? options.opener
        : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

    if (this.modalTitle) this.modalTitle.textContent = product.title || '';
    if (this.modalVendor) this.modalVendor.textContent = [product.vendor, product.type].filter(Boolean).join(' / ');
    if (this.modalPrice) this.modalPrice.innerHTML = this.formatProductPrice(product);
    if (this.modalDescription) {
      this.modalDescription.innerHTML = detailsAlreadyLoaded
        ? product.descriptionHtml || '<p>No description available.</p>'
        : '<p>Loading product details…</p>';
    }
    if (this.modalLink) this.modalLink.href = product.url || '#';

    this.setModalPanel('images');
    this.renderModalGallery(product);
    this.renderModalDots(product);
    this.showModalImage(0, { instant: true, preloadNeighbors: false });
    this.resetModalScroll();
    this.prepareModalHeaderState();
    this.classList.add('hp-modal-open');
    document.body.classList.add('hp-collection-modal-open');
    this.lockScroll();
    this.modal.removeAttribute('inert');
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    this.syncModalBackgroundInert();
    document.addEventListener('keydown', this.boundKeydown);
    window.requestAnimationFrame(() => {
      this.activeImageIndex = 0;
      this.resetModalTrackPosition(0);
      this.updateModalImageControls(this.modalImages, 0);
      this.resetModalScroll();
      this.preloadImageModalNeighbors('high');
      this.preloadImageModalGallery('auto');
      this.schedulePersistentScrollbarSync();
    });
    if (!detailsAlreadyLoaded) {
      this.loadProductDetails(product).then((loadedProduct) => {
        if (this.activeProduct !== product || !this.modal?.classList.contains('is-open')) return;

        if (this.modalDescription) {
          this.modalDescription.innerHTML = loadedProduct.descriptionHtml || '<p>No description available.</p>';
        }
        this.renderModalGallery(loadedProduct);
        this.renderModalDots(loadedProduct);
        this.showModalImage(0, { instant: true });
        this.resetModalScroll();
        this.preloadImageModalGallery('auto');
        this.schedulePersistentScrollbarSync();
      });
    }
    const focusTarget = options.focusClose
      ? this.modal.querySelector('.hp-product-modal__close')
      : this.modal.querySelector('.hp-product-modal__content');
    focusTarget?.focus?.({ preventScroll: true });
  }

  closeModal({ restoreFocus = false } = {}) {
    if (!this.modal) return;
    const wasOpen = this.modal.classList.contains('is-open') || this.modal.getAttribute('aria-hidden') === 'false' || this.classList.contains('hp-modal-open');
    const focusTarget = this.lastFocusedElement;
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    this.modal.setAttribute('inert', '');
    const closesAllModals = !this.buyingGuideModal?.classList.contains('is-open');
    if (closesAllModals) {
      this.classList.remove('hp-modal-open');
      document.removeEventListener('keydown', this.boundKeydown);
    }
    this.syncModalBackgroundInert();
    this.lastFocusedElement = null;
    this.activeProduct = null;
    this.activeImageIndex = 0;
    this.modalImages = [];
    this.modalImageRequestId += 1;
    this.activeModalCardImageSrcs = [];
    this.resetModalImageSwipe();
    if (this.modalTrackScrollFrame) {
      window.cancelAnimationFrame(this.modalTrackScrollFrame);
      this.modalTrackScrollFrame = 0;
    }
    window.requestAnimationFrame(() => {
      if (wasOpen && closesAllModals) this.unlockScroll();
      if (closesAllModals) {
        document.body.classList.remove('hp-collection-modal-open');
        this.clearModalHeaderState();
      }
      if (wasOpen && restoreFocus) focusTarget?.focus?.({ preventScroll: true });
      this.setModalPanel('images');
    });
  }

  handleKeydown(event) {
    if (this.buyingGuideModal?.classList.contains('is-open')) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeBuyingGuide({ restoreFocus: true });
        return;
      }
      if (event.key === 'Tab') this.trapModalFocus(event, this.buyingGuideModal);
      return;
    }

    if (!this.modal?.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeModal({ restoreFocus: true });
    }
    if (event.key === 'Tab') this.trapModalFocus(event, this.modal);
    if (event.key === 'ArrowLeft') this.showModalImage(this.activeImageIndex - 1);
    if (event.key === 'ArrowRight') this.showModalImage(this.activeImageIndex + 1);
  }

  trapModalFocus(event, modal) {
    if (!(modal instanceof HTMLElement)) return;
    const focusable = Array.from(modal.querySelectorAll(
      'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => (
      element instanceof HTMLElement
      && !element.hasAttribute('hidden')
      && element.getAttribute('aria-hidden') !== 'true'
      && element.getClientRects().length > 0
    ));
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!focusable.includes(document.activeElement)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  resetModalScroll() {
    if (!this.modal) return;
    this.modalTouchY = 0;
    this.resetModalImageSwipe();
    this.modal.querySelectorAll('.hp-product-modal__content, .hp-product-modal__details, .hp-product-modal__description').forEach((element) => {
      if (element instanceof HTMLElement) element.scrollTop = 0;
    });
  }

  setModalPanel(panel = 'images') {
    if (!this.modal) return;
    const nextPanel = panel === 'details' ? 'details' : 'images';
    this.modal.setAttribute('data-hp-modal-view', nextPanel);
    this.modal.querySelectorAll('[data-hp-modal-panel-toggle]').forEach((button) => {
      const isActive = button.getAttribute('data-panel') === nextPanel;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    this.resetModalScroll();
    this.schedulePersistentScrollbarSync();
  }

  captureModalImageSwipeStart(event) {
    if (!this.modal?.classList.contains('is-open')) return;
    if (this.modal.getAttribute('data-hp-modal-view') !== 'images') return;
    if (this.isModalTrackSwipeLayout()) return;
    if (event.button !== undefined && event.button !== 0) return;
    if (event.isPrimary === false) return;
    if (event.target instanceof Element && event.target.closest('.hp-product-modal__nav, .hp-product-modal__dot')) return;
    if (this.startModalImageSwipe(event.clientX, event.clientY, event.pointerId ?? null)) {
      event.currentTarget?.setPointerCapture?.(event.pointerId);
    }
  }

  trackModalImageSwipe(event) {
    this.trackModalImageSwipePosition(event.clientX, event.clientY, event.pointerId ?? null, event);
  }

  handleModalImageSwipeEnd(event) {
    if (event.currentTarget?.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    this.finishModalImageSwipe(event.pointerId ?? null);
  }

  handleModalImageWheel(event) {
    if (!this.modal?.classList.contains('is-open') || !this.activeProduct) return;
    if (this.modal.getAttribute('data-hp-modal-view') !== 'images') return;
    if (this.isModalTrackSwipeLayout()) return;
    if (event.target instanceof Element && event.target.closest('.hp-product-modal__nav, .hp-product-modal__dot')) return;

    const deltaX = event.deltaX || 0;
    const deltaY = event.deltaY || 0;
    if (Math.abs(deltaX) < 28 || Math.abs(deltaX) < Math.abs(deltaY) * 1.15) return;

    event.preventDefault();
    const now = Date.now();
    if (now - this.lastModalWheelTime < 420) return;
    this.lastModalWheelTime = now;
    this.showModalImage(this.activeImageIndex + (deltaX > 0 ? 1 : -1));
  }

  startModalImageSwipe(clientX, clientY, pointerId) {
    if (!this.modal?.classList.contains('is-open')) return false;
    if (this.modal.getAttribute('data-hp-modal-view') !== 'images') return false;
    if (this.modalImageSwipe) return false;
    if (this.modalImages.length < 2) return false;
    this.modalSwipeStartX = clientX;
    this.modalSwipeStartY = clientY;
    this.modalSwipePointerId = pointerId;
    this.modalImageSwipe = {
      pointerId,
      startX: clientX,
      startY: clientY,
      lastX: clientX,
      lastY: clientY,
      swiping: false,
    };
    return true;
  }

  trackModalImageSwipePosition(clientX, clientY, pointerId, event) {
    if (!this.modal?.classList.contains('is-open')) return;
    if (this.modal.getAttribute('data-hp-modal-view') !== 'images') return;
    const swipe = this.modalImageSwipe;
    if (!swipe || (swipe.pointerId !== null && pointerId !== swipe.pointerId)) return;

    swipe.lastX = clientX;
    swipe.lastY = clientY;
    const deltaX = swipe.lastX - swipe.startX;
    const deltaY = swipe.lastY - swipe.startY;
    if (!swipe.swiping && Math.abs(deltaX) > 14 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
      swipe.swiping = true;
    }
    if (swipe.swiping) {
      event?.preventDefault?.();
      const maxOffset = Math.max(18, Math.min(52, window.innerWidth * .09));
      const offset = Math.max(-maxOffset, Math.min(maxOffset, deltaX * .18));
      this.setModalImageDragOffset(offset, true);
      const previewSwipe = Math.max(18, Math.min(34, window.innerWidth * .045));
      const previewIndex = Math.abs(deltaX) >= previewSwipe
        ? this.activeImageIndex + (deltaX < 0 ? 1 : -1)
        : this.activeImageIndex;
      this.updateModalImageControls(this.modalImages, previewIndex);
    }
  }

  finishModalImageSwipe(pointerId) {
    if (!this.modal?.classList.contains('is-open') || !this.activeProduct) return;
    if (this.modal.getAttribute('data-hp-modal-view') !== 'images') return;
    const swipe = this.modalImageSwipe;
    if (!swipe || (swipe.pointerId !== null && pointerId !== swipe.pointerId)) return;

    const deltaX = swipe.lastX - swipe.startX;
    const deltaY = swipe.lastY - swipe.startY;
    this.resetModalImageSwipe();

    const minimumSwipe = Math.max(34, Math.min(58, window.innerWidth * .08));
    if (Math.abs(deltaX) < minimumSwipe || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) {
      this.updateModalImageControls(this.modalImages, this.activeImageIndex);
      return;
    }

    this.showModalImage(this.activeImageIndex + (deltaX < 0 ? 1 : -1));
  }

  resetModalImageSwipe() {
    this.modalSwipeStartX = 0;
    this.modalSwipeStartY = 0;
    this.modalSwipePointerId = null;
    this.modalImageSwipe = null;
    this.setModalImageDragOffset(0, false);
  }

  setModalImageDragOffset(offset = 0, active = false) {
    const target = this.modalTrack instanceof HTMLElement ? this.modalTrack : this.modalImage;
    if (!(target instanceof HTMLElement)) return;
    target.style.transition = active ? 'none' : '';
    target.style.transform = offset ? `translateX(${Math.round(offset)}px)` : '';
  }

  getProductImages(product) {
    const images = Array.isArray(product?.images) ? product.images.filter((image) => image?.src || image?.previewSrc) : [];
    if (images.length) return images;
    if (product?.featuredImage) return [{ src: product.featuredImage, previewSrc: product.featuredImage, alt: product.title }];
    return [];
  }

  normalizeImageSrc(src) {
    const value = src ? String(src) : '';
    if (!value) return '';
    if (value.startsWith('//')) return `${window.location.protocol}${value}`;
    return value;
  }

  getSizedShopifyImageSrc(src, width) {
    const normalizedSrc = this.normalizeImageSrc(src);
    if (!normalizedSrc || !width) return normalizedSrc;

    try {
      const url = new URL(normalizedSrc, window.location.origin);
      if (url.hostname.includes('cdn.shopify.com') || url.pathname.includes('/cdn/shop/')) {
        url.searchParams.set('width', String(width));
        return url.toString();
      }
    } catch (error) {
      return normalizedSrc;
    }

    return normalizedSrc;
  }

  getCardGalleryImageSources(card) {
    if (!(card instanceof HTMLElement)) return [];

    const images = Array.from(card.querySelectorAll('.hp-product-card__media img'));
    return images.reduce((sources, image) => {
      if (!(image instanceof HTMLImageElement)) return sources;
      if (!image.complete || image.naturalWidth <= 0) return sources;

      const index = Number.parseInt(image.getAttribute('data-index') || String(sources.length), 10);
      const src = this.normalizeImageSrc(image.currentSrc || image.src || '');
      if (!src) return sources;

      this.loadedModalImages.add(src);
      sources[Number.isFinite(index) ? index : sources.length] = src;
      return sources;
    }, []);
  }

  setModalImageFetchPriority(image, priority = 'auto') {
    if (!(image instanceof HTMLImageElement)) return;

    if ('fetchPriority' in image) image.fetchPriority = priority;
    else image.setAttribute('fetchpriority', priority);
  }

  preloadModalImage(src, priority = 'auto') {
    const normalizedSrc = this.normalizeImageSrc(src);
    if (!normalizedSrc || normalizedSrc === 'null') return Promise.resolve(null);
    if (this.loadedModalImages.has(normalizedSrc)) return Promise.resolve(normalizedSrc);
    if (this.modalImagePreloadPromises.has(normalizedSrc)) return this.modalImagePreloadPromises.get(normalizedSrc);

    const preloadPromise = new Promise((resolve) => {
      const image = new Image();
      let settled = false;
      const finish = (loaded) => {
        if (settled) return;
        settled = true;
        this.modalImagePreloadPromises.delete(normalizedSrc);
        if (loaded) this.loadedModalImages.add(normalizedSrc);
        resolve(loaded ? normalizedSrc : null);
      };
      image.decoding = 'async';
      this.setModalImageFetchPriority(image, priority);
      image.onload = () => {
        if (typeof image.decode !== 'function') {
          finish(true);
          return;
        }
        image.decode().then(() => finish(true)).catch(() => finish(true));
      };
      image.onerror = () => finish(false);
      image.src = normalizedSrc;
      if (image.complete && image.naturalWidth > 0) image.onload();
    });

    this.modalImagePreloadPromises.set(normalizedSrc, preloadPromise);
    return preloadPromise;
  }

  getProductModalImages(product, cardSources = []) {
    const seen = new Set();
    return this.getProductImages(product).map((image, index) => {
      const fullSrc = this.normalizeImageSrc(image.src || image.previewSrc || '');
      const cardSrc = this.normalizeImageSrc(cardSources[index] || '');
      const previewSrc = cardSrc || this.normalizeImageSrc(image.previewSrc || this.getSizedShopifyImageSrc(fullSrc, 520));
      const lowSrc = this.getSizedShopifyImageSrc(previewSrc || fullSrc, 360);
      if (!fullSrc || seen.has(fullSrc)) return null;
      seen.add(fullSrc);
      if (cardSrc) this.loadedModalImages.add(cardSrc);
      return {
        src: fullSrc,
        previewSrc: previewSrc || fullSrc,
        lowSrc,
        alt: image.alt || product?.title || 'Product image',
      };
    }).filter(Boolean);
  }

  primeModalImage(image, priority = 'auto') {
    if (!image) return;
    this.preloadModalImage(image.previewSrc || image.lowSrc || image.src, priority);
  }

  primeProductModalGallery(product, priority = 'auto', card = null) {
    const cardSources = this.getCardGalleryImageSources(card);
    this.getProductModalImages(product, cardSources).forEach((image) => this.primeModalImage(image, priority));
  }

  preloadImageModalNeighbors(priority = 'auto') {
    if (this.modalImages.length < 2) return;
    [this.activeImageIndex - 1, this.activeImageIndex + 1, this.activeImageIndex + 2].forEach((index) => {
      const image = this.modalImages[index];
      if (!image) return;
      this.primeModalImage(image, priority);
    });
  }

  preloadImageModalGallery(priority = 'auto') {
    this.modalImages.forEach((image) => this.primeModalImage(image, priority));
  }

  formatProductPrice(product) {
    const min = Number(product?.priceMin || 0);
    const max = Number(product?.priceMax || min);
    if (this.memberDiscount <= 0) {
      return this.escapeHtml(max > min ? `From ${this.formatMoney(min)}` : this.formatMoney(min));
    }

    const memberPrice = Math.round((min * (100 - this.memberDiscount)) / 100);
    const prefix = max > min ? 'From&nbsp;' : '';
    return `
      <span class="hp-member-price" data-hp-member-price data-member-discount="${this.memberDiscount}" data-member-tier="${this.escapeHtml(this.memberTier)}">
        <span class="hp-member-price__amounts">
          <span class="visually-hidden">Store price:</span>
          <s class="hp-member-price__store">${prefix}${this.escapeHtml(this.formatMoney(min))}</s>
          <span class="visually-hidden">Your member price:</span>
          <strong class="hp-member-price__value">${prefix}${this.escapeHtml(this.formatMoney(memberPrice))}</strong>
        </span>
        <span class="hp-member-price__badge"><span class="hp-member-price__tier">${this.escapeHtml(this.memberTier)} Tier</span><span class="hp-member-price__separator" aria-hidden="true">·</span><span>${this.memberDiscount}% off</span></span>
      </span>
    `;
  }

  renderModalGallery(product) {
    const images = this.getProductModalImages(product, this.activeModalCardImageSrcs);
    this.modalImages = images;

    if (this.modalTrack instanceof HTMLElement) {
      this.modalTrack.innerHTML = images.length
        ? images.map((image, index) => {
          const source = image.previewSrc || image.lowSrc || image.src || '';
          const isFirst = index === 0;
          return `
            <span class="hp-product-modal__media-slide" data-hp-modal-slide data-index="${index}">
              <img
                src="${this.escapeHtml(source)}"
                alt="${this.escapeHtml(image.alt || product?.title || 'Product image')}"
                class="hp-product-modal__image"
                ${isFirst ? 'data-hp-modal-image' : ''}
                data-hp-modal-image-slide
                data-index="${index}"
                width="1600"
                height="1600"
                loading="${isFirst ? 'eager' : 'lazy'}"
                decoding="async"
                fetchpriority="${isFirst ? 'high' : 'auto'}"
              >
            </span>
          `;
        }).join('')
        : '';
      this.resetModalTrackPosition(0);
      this.modalImage = this.modalTrack.querySelector('[data-hp-modal-image]') || this.modalTrack.querySelector('[data-hp-modal-image-slide]');
    }

    if (this.modalImage instanceof HTMLImageElement) {
      this.modalImage.hidden = images.length === 0;
      this.modalImage.alt = product?.title || '';
      if (!images.length) this.modalImage.removeAttribute('src');
    }

    this.updateModalImageControls(images, 0);
  }

  showModalImage(index, options = {}) {
    const images = this.modalImages;
    if (!images.length) {
      this.updateModalImageControls(images, 0);
      return;
    }

    const nextIndex = Math.max(0, Math.min(images.length - 1, index));
    const image = images[nextIndex];
    const imageElement = this.getModalSlideImage(nextIndex) || this.modalImage;
    this.modalImageRequestId += 1;
    const instantSrc = this.loadedModalImages.has(image.src)
      ? image.src
      : image.previewSrc || image.lowSrc || image.src;

    this.activeImageIndex = nextIndex;
    this.setModalImageDragOffset(0, false);
    if (imageElement instanceof HTMLImageElement) {
      this.modalImage = imageElement;
      this.modalImage.hidden = false;
      this.modalImage.removeAttribute('srcset');
      this.modalImage.removeAttribute('sizes');
      this.modalImage.alt = image.alt || this.activeProduct?.title || 'Product image';
      this.modalImage.loading = 'eager';
      this.modalImage.decoding = 'async';
      this.setModalImageFetchPriority(this.modalImage, 'high');
      if (instantSrc && this.modalImage.getAttribute('src') !== instantSrc) this.modalImage.src = instantSrc;
    }
    this.scrollModalTrackToIndex(nextIndex, { instant: options.instant === true });
    this.updateModalImageControls(images, nextIndex);
    if (options.preloadNeighbors !== false) this.preloadImageModalNeighbors('high');

    if (!image.src || this.loadedModalImages.has(image.src)) return;
    this.preloadModalImage(image.src, 'high');
  }

  isModalTrackSwipeLayout() {
    return Boolean(window.matchMedia?.(
      '(max-width: 989px), (max-height: 640px), (min-width: 990px) and (max-width: 1180px) and (hover: none), (min-width: 990px) and (max-width: 1180px) and (pointer: coarse)'
    ).matches);
  }

  getModalSlideImage(index) {
    if (!(this.modalTrack instanceof HTMLElement)) return null;
    return this.modalTrack.querySelector(`[data-hp-modal-image-slide][data-index="${index}"]`);
  }

  resetModalTrackPosition(index = 0) {
    if (!(this.modalTrack instanceof HTMLElement)) return;
    const left = this.modalTrack.clientWidth * index;
    if (this.modalTrackScrollFrame) {
      window.cancelAnimationFrame(this.modalTrackScrollFrame);
      this.modalTrackScrollFrame = 0;
    }
    this.modalTrack.scrollLeft = left;
    this.modalTrack.scrollTo?.({ left, behavior: 'auto' });
  }

  scrollModalTrackToIndex(index, options = {}) {
    if (!(this.modalTrack instanceof HTMLElement)) return;
    const left = this.modalTrack.clientWidth * index;
    if (options.instant === true) {
      this.resetModalTrackPosition(index);
      return;
    }
    this.modalTrack.scrollLeft = left;
    this.modalTrack.scrollTo?.({ left, behavior: 'auto' });
  }

  syncModalImageFromTrack() {
    if (!(this.modalTrack instanceof HTMLElement) || !this.modalImages.length) return;
    if (this.modalTrackScrollFrame) return;

    this.modalTrackScrollFrame = window.requestAnimationFrame(() => {
      this.modalTrackScrollFrame = 0;
      const width = Math.max(this.modalTrack.clientWidth, 1);
      const nextIndex = Math.max(0, Math.min(this.modalImages.length - 1, Math.round(this.modalTrack.scrollLeft / width)));
      if (nextIndex === this.activeImageIndex) return;
      this.activeImageIndex = nextIndex;
      const imageElement = this.getModalSlideImage(nextIndex);
      if (imageElement instanceof HTMLImageElement) this.modalImage = imageElement;
      this.updateModalImageControls(this.modalImages, nextIndex);
      this.preloadImageModalNeighbors('high');
    });
  }

  updateModalImageControls(images, nextIndex) {
    if (this.modalPrev) {
      this.modalPrev.hidden = images.length <= 1;
      this.modalPrev.disabled = nextIndex <= 0;
      this.modalPrev.classList.toggle('is-muted', nextIndex <= 0);
    }
    if (this.modalNext) {
      this.modalNext.hidden = images.length <= 1;
      this.modalNext.disabled = nextIndex >= images.length - 1;
      this.modalNext.classList.toggle('is-muted', nextIndex >= images.length - 1);
    }
    this.updateModalDots();
  }

  renderModalDots(product) {
    if (!this.modalDots) return;
    const images = this.modalImages.length ? this.modalImages : this.getProductModalImages(product, this.activeModalCardImageSrcs);
    this.modalDots.hidden = images.length <= 1;
    this.modalDots.innerHTML = images.map((_, index) => (
      `<button type="button" class="hp-product-modal__dot" data-hp-modal-dot data-index="${index}" aria-label="Show image ${index + 1}"></button>`
    )).join('');
    this.updateModalDots();
  }

  updateModalDots() {
    this.modalDots?.querySelectorAll('[data-hp-modal-dot]').forEach((dot) => {
      dot.classList.toggle('is-active', Number(dot.getAttribute('data-index')) === this.activeImageIndex);
    });
  }

  getCardVariantId(card) {
    const productId = card?.getAttribute('data-product-id');
    const product = this.productMap.get(String(productId || ''));
    const select = card?.querySelector('[data-hp-card-variant]');
    if (select instanceof HTMLSelectElement && select.value) return select.value;
    const variant = (product?.variants || []).find((entry) => entry.available) || product?.variants?.[0];
    return variant?.id;
  }

  getCardProduct(card) {
    const productId = card?.getAttribute('data-product-id');
    return this.productMap.get(String(productId || '')) || null;
  }

  getCardSelectedVariant(card, variantId = this.getCardVariantId(card)) {
    const product = this.getCardProduct(card);
    return (product?.variants || []).find((variant) => String(variant.id) === String(variantId)) || null;
  }

  getVariantMaxQuantity(variant) {
    if (!variant || !variant.inventoryManagement) return null;
    if (variant.inventoryPolicy !== 'deny') return null;

    const inventoryQuantity = Number(variant.inventoryQuantity);
    if (!Number.isFinite(inventoryQuantity)) return null;

    const quantityRuleMax = this.parseOptionalNumber(variant.quantityRuleMax);
    const maxQuantity = quantityRuleMax !== null
      ? Math.min(inventoryQuantity, quantityRuleMax)
      : inventoryQuantity;

    return Math.max(0, maxQuantity);
  }

  parseOptionalNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  getCartVariantQuantityFromCart(cart, variantId) {
    if (!cart || !Array.isArray(cart.items) || !variantId) return 0;

    return cart.items.reduce((sum, item) => {
      const itemVariantId = item?.variant_id ?? item?.variantId;
      if (String(itemVariantId) !== String(variantId)) return sum;
      return sum + (Number(item.quantity) || 0);
    }, 0);
  }

  async refreshCartQuantities({ updateButtons = false, force = false } = {}) {
    if (this.cartQuantityRequest && !force) return this.cartQuantityRequest;

    const requestVersion = this.cartStateVersion;
    this.cartQuantityRequest = fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (cart && Array.isArray(cart.items) && requestVersion === this.cartStateVersion) {
          this.latestCart = cart;
          if (updateButtons) this.updateAddButtonStates();
        }
        return cart;
      })
      .catch(() => null)
      .finally(() => {
        this.cartQuantityRequest = null;
      });

    return this.cartQuantityRequest;
  }

  async getCartVariantQuantity(variantId) {
    const cart = await this.refreshCartQuantities({ force: true });
    return this.getCartVariantQuantityFromCart(cart || this.latestCart, variantId);
  }

  bumpCachedCartVariantQuantity(variantId, quantity = 1) {
    if (!variantId) return;

    const cart = this.latestCart && Array.isArray(this.latestCart.items)
      ? { ...this.latestCart, items: this.latestCart.items.map((item) => ({ ...item })) }
      : { items: [], item_count: 0 };

    const existingItem = cart.items.find((item) => String(item.variant_id ?? item.variantId) === String(variantId));
    if (existingItem) {
      existingItem.quantity = (Number(existingItem.quantity) || 0) + quantity;
    } else {
      cart.items.push({ variant_id: Number(variantId), quantity });
    }
    cart.item_count = (Number(cart.item_count) || 0) + quantity;
    this.latestCart = cart;
  }

  handleCartStateUpdate(event) {
    const cart = event?.detail?.resource || event?.detail?.cart;
    if (cart && Array.isArray(cart.items)) {
      this.cartStateVersion += 1;
      this.latestCart = cart;
      this.updateAddButtonStates();
      return;
    }

    this.refreshCartQuantities({ updateButtons: true });
  }

  updateAddButtonStates() {
    this.querySelectorAll('[data-hp-card]').forEach((card) => {
      const button = card.querySelector('[data-hp-card-add]');
      if (!(button instanceof HTMLButtonElement)) return;
      if (button.getAttribute('aria-busy') === 'true') return;
      this.storeDefaultButtonLabel(button);

      const variantId = this.getCardVariantId(card);
      const variant = this.getCardSelectedVariant(card, variantId);
      const maxQuantity = this.getVariantMaxQuantity(variant);
      const currentQuantity = this.getCartVariantQuantityFromCart(this.latestCart, variantId);
      const isUnavailable = !variant || variant.available === false;
      const isAtLimit = maxQuantity !== null && currentQuantity >= maxQuantity;

      button.disabled = isUnavailable;
      button.toggleAttribute('aria-disabled', button.disabled);
      if (isAtLimit) this.setButtonLimitState(button);
      else this.clearButtonLimitState(button);
    });
  }

  getCardCartImage(card) {
    const product = this.getCardProduct(card);
    const imageElement = card?.querySelector('.hp-product-card__media img');
    return imageElement instanceof HTMLImageElement
      ? this.normalizeImageSrc(imageElement.currentSrc || imageElement.src || product?.featuredImage || '')
      : this.normalizeImageSrc(product?.featuredImage || '');
  }

  getInventoryLimitMessage(maxQuantity) {
    return maxQuantity === 1
      ? 'Only 1 available for this item.'
      : `Only ${maxQuantity} available for this item.`;
  }

  showCartToast(message, type = 'error') {
    if (!message) return;
    const existing = document.querySelector('[data-hp-collection-toast]');
    const toast = existing instanceof HTMLElement ? existing : document.createElement('div');
    toast.className = `hp-collection-toast is-${type}`;
    toast.setAttribute('data-hp-collection-toast', '');
    if (type === 'error') {
      toast.setAttribute('role', 'alert');
      toast.removeAttribute('aria-live');
    } else {
      toast.setAttribute('aria-live', 'polite');
      toast.removeAttribute('role');
    }
    toast.textContent = message;
    if (!existing) document.body.append(toast);

    window.clearTimeout(Number(toast.dataset.hpTimer || 0));
    toast.classList.add('is-visible');
    window.requestAnimationFrame(() => toast.classList.add('is-visible'));
    const timer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => {
        if (toast.isConnected && !toast.classList.contains('is-visible')) toast.remove();
      }, 180);
    }, type === 'error' ? 4600 : 1800);
    toast.dataset.hpTimer = String(timer);
  }

  clearCardCartMessage(card) {
    if (!(card instanceof HTMLElement)) return;
    const messageElement = card.querySelector('[data-hp-card-cart-message]');
    if (!(messageElement instanceof HTMLElement)) return;
    window.clearTimeout(Number(messageElement.dataset.hpTimer || 0));
    messageElement.remove();
  }

  getPendingCartItem(card, variantId, variant, maxQuantity, cardImage) {
    const product = this.getCardProduct(card);
    const cardTitleElement = card?.querySelector('.hp-product-card__title a');
    const cardTitle = cardTitleElement?.dataset?.hpOriginalTitle
      || cardTitleElement?.getAttribute('aria-label')
      || card?.querySelector('.hp-product-card__title')?.textContent?.trim();
    const productTitle = product?.title || cardTitle || 'Product';
    const variantTitle = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
    const optionNames = Array.isArray(product?.optionNames) ? product.optionNames : [];
    const optionValues = Array.isArray(variant?.options) ? variant.options : [];
    const optionsWithValues = optionValues.map((value, index) => ({
      name: optionNames[index] || `Option ${index + 1}`,
      value,
    })).filter((option) => option.value && option.value !== 'Default Title');
    const numericVariantId = Number(variantId);
    const normalizedVariantId = Number.isFinite(numericVariantId) ? numericVariantId : String(variantId);
    const price = Number(variant?.price) || 0;
    const pendingItem = {
      id: normalizedVariantId,
      variant_id: normalizedVariantId,
      variantId: String(variantId),
      product_id: product?.id || card?.getAttribute('data-product-id') || '',
      product_title: productTitle,
      title: productTitle,
      variant_title: variantTitle,
      url: product?.url || window.location.pathname,
      quantity: 1,
      price,
      final_line_price: price,
      image: cardImage || product?.featuredImage || null,
      optimistic: true,
    };

    if (optionsWithValues.length) pendingItem.options_with_values = optionsWithValues;

    if (variant) {
      pendingItem.inventory_management = variant.inventoryManagement || null;
      pendingItem.inventoryManagement = variant.inventoryManagement || null;
      pendingItem.inventory_policy = variant.inventoryPolicy || null;
      pendingItem.inventoryPolicy = variant.inventoryPolicy || null;

      const inventoryQuantity = this.parseOptionalNumber(variant.inventoryQuantity);
      if (inventoryQuantity !== null) {
        pendingItem.inventory_quantity = inventoryQuantity;
        pendingItem.inventoryQuantity = inventoryQuantity;
      }
    }

    if (maxQuantity !== null) {
      pendingItem.quantity_rule_max = maxQuantity;
      pendingItem.quantityRuleMax = maxQuantity;
    }

    return pendingItem;
  }

  dispatchCollectionCartEvent(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail: {
        sourceId: detail.sourceId || 'collection-card',
        data: {
          source: 'collection-card',
          itemCount: 1,
          ...detail.data,
        },
      },
    }));
  }

  async addCardToCart(card, button) {
    const variantId = this.getCardVariantId(card);
    await this.addToCart(variantId, button, card);
  }

  async waitForCartClear() {
    const clearPromise = window.HalfpipedCartClearPromise;
    if (!clearPromise || typeof clearPromise.then !== 'function') return;
    await clearPromise.catch(() => {});
    await this.refreshCartQuantities({ updateButtons: true, force: true });
  }

  async addToCart(variantId, button, card = null) {
    if (!variantId || !(button instanceof HTMLElement)) return;
    if (button.dataset.hpAddPending === 'true') return;
    this.storeDefaultButtonLabel(button);
    const originalText = button.querySelector('[data-hp-button-label]')?.textContent || button.textContent;
    const originalDisabled = button.hasAttribute('disabled');
    const sourceId = `collection-card-${variantId}`;
    const productId = card?.getAttribute('data-product-id') || '';
    const selectedVariant = this.getCardSelectedVariant(card, variantId);
    const maxQuantity = this.getVariantMaxQuantity(selectedVariant);
    const cardImage = this.getCardCartImage(card);
    button.dataset.hpAddPending = 'true';
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('disabled', '');
    button.classList.remove('is-success', 'is-limit');
    this.clearButtonShadowRelease(button);
    this.animateButtonPress(button);
    this.clearCardCartMessage(card);
    let didOpenOptimisticCart = false;
    let willReachLimit = false;

    try {
      await this.waitForCartClear();

      if (maxQuantity !== null) {
        const cartQuantity = this.getCartVariantQuantityFromCart(this.latestCart, variantId);
        if (cartQuantity >= maxQuantity) {
          const limitError = new Error(this.getInventoryLimitMessage(maxQuantity));
          limitError.skipCartDrawer = true;
          throw limitError;
        }
        willReachLimit = cartQuantity + 1 >= maxQuantity;
      }

      const pendingItem = this.getPendingCartItem(card, variantId, selectedVariant, maxQuantity, cardImage);
      button.classList.add('is-success');
      this.setButtonLabel(button, 'Added');
      this.dispatchCollectionCartEvent('cart:add:pending', {
        sourceId,
        data: {
          variantId: String(variantId),
          productId,
          pendingItem,
          cardImage,
          variantMaxQuantity: maxQuantity,
        },
      });
      didOpenOptimisticCart = true;
      if (willReachLimit) this.setButtonLimitState(button, { enable: !originalDisabled });
      else this.resetAddButtonVisual(button, originalText, { enable: !originalDisabled });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });
      const addedItem = await response.json().catch(() => null);
      if (!response.ok) throw new Error(addedItem?.description || addedItem?.message || 'Add to cart failed');
      this.dispatchCollectionCartEvent('cart:update', {
        sourceId,
        data: {
          variantId: String(variantId),
          productId,
          addedItem,
          cardImage,
          variantMaxQuantity: maxQuantity,
        },
      });
      this.bumpCachedCartVariantQuantity(variantId, 1);
      this.updateAddButtonStates();
    } catch (error) {
      if (!error?.skipCartDrawer) console.error(error);
      const message = error?.message || 'Could not add this item to cart. Please try again.';
      if (!error?.skipCartDrawer || didOpenOptimisticCart) {
        this.dispatchCollectionCartEvent('cart:error', {
          sourceId,
          data: {
            variantId: String(variantId),
            productId,
            message,
          },
        });
      }
      this.resetAddButtonVisual(button, originalText);
      if (!didOpenOptimisticCart) {
        if (maxQuantity !== null) this.setButtonLimitState(button, { enable: !originalDisabled });
        else this.setButtonLabel(button, 'Try again');
        window.setTimeout(() => {
          this.resetAddButtonVisual(button, originalText);
          this.updateAddButtonStates();
        }, 1400);
      }
    } finally {
      delete button.dataset.hpAddPending;
      button.removeAttribute('aria-busy');
      if (!originalDisabled) button.removeAttribute('disabled');
      this.updateAddButtonStates();
    }
  }

  setButtonLabel(button, label) {
    const target = button.querySelector('[data-hp-button-label]') || button.querySelector('.hp-btn__text span:last-child');
    if (target) target.textContent = label;
    else button.textContent = label;
  }

  storeDefaultButtonLabel(button) {
    if (!(button instanceof HTMLElement) || button.dataset.hpDefaultLabel) return;
    const label = button.querySelector('[data-hp-button-label]')?.textContent || button.textContent;
    button.dataset.hpDefaultLabel = label?.trim() || 'Add to cart';
  }

  setButtonLimitState(button, { enable = false } = {}) {
    this.clearButtonPressAnimation(button);
    button.classList.remove('is-success');
    button.classList.add('is-limit');
    button.setAttribute('data-inventory-limited', 'true');
    button.removeAttribute('aria-busy');
    if (enable) button.removeAttribute('disabled');
    this.setButtonLabel(button, 'Max in cart');
  }

  clearButtonLimitState(button) {
    button.classList.remove('is-limit');
    button.removeAttribute('data-inventory-limited');
    this.clearButtonShadowRelease(button);
    this.setButtonLabel(button, button.dataset.hpDefaultLabel || 'Add to cart');
  }

  resetAddButtonVisual(button, label, options = {}) {
    button.classList.remove('is-success', 'is-limit');
    button.removeAttribute('data-inventory-limited');
    button.removeAttribute('aria-busy');
    this.clearButtonShadowRelease(button);
    if (options.enable) button.removeAttribute('disabled');
    this.setButtonLabel(button, label?.trim() || 'Add to cart');
  }

  animateButtonPress(button, duration = 280) {
    if (!(button instanceof HTMLElement)) return;

    const previousTimer = Number(button.dataset.hpPressAnimationTimer || 0);
    if (previousTimer) window.clearTimeout(previousTimer);

    button.classList.add('is-press-animating');
    const timer = window.setTimeout(() => {
      button.classList.remove('is-press-animating');
      delete button.dataset.hpPressAnimationTimer;
    }, duration);
    button.dataset.hpPressAnimationTimer = String(timer);
  }

  clearButtonPressAnimation(button) {
    if (!(button instanceof HTMLElement)) return;

    const timer = Number(button.dataset.hpPressAnimationTimer || 0);
    if (timer) window.clearTimeout(timer);
    delete button.dataset.hpPressAnimationTimer;
    button.classList.remove('is-press-animating');
  }

  scheduleButtonShadowRelease(button, delay = 180) {
    if (!(button instanceof HTMLElement)) return;
    if (button.classList.contains('is-shadow-released') || button.dataset.hpShadowReleaseTimer) return;

    const timer = window.setTimeout(() => {
      button.classList.add('is-shadow-released');
      delete button.dataset.hpShadowReleaseTimer;
    }, delay);
    button.dataset.hpShadowReleaseTimer = String(timer);
  }

  clearButtonShadowRelease(button) {
    if (!(button instanceof HTMLElement)) return;

    const timer = Number(button.dataset.hpShadowReleaseTimer || 0);
    if (timer) window.clearTimeout(timer);
    delete button.dataset.hpShadowReleaseTimer;
    button.classList.remove('is-shadow-released');
  }

  async refreshCartCount() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const count = Number(cart?.item_count || 0);
      document.querySelectorAll('[data-cart-count-badge]').forEach((badge) => {
        badge.textContent = count > 99 ? '99+' : String(count);
        badge.toggleAttribute('hidden', count <= 0);
      });
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
    } catch (error) {
      // Count refresh is best-effort; cart add already succeeded.
    }
  }

  lockScroll() {
    this.scrollLockY = window.scrollY || document.documentElement.scrollTop || 0;
    this.previousScrollBehavior = document.documentElement.style.scrollBehavior;
    this.previousDocumentOverflow = document.documentElement.style.overflow;
    this.previousDocumentOverscrollBehavior = document.documentElement.style.overscrollBehavior;
    this.previousDocumentScrollbarGutter = document.documentElement.style.scrollbarGutter;
    this.previousBodyOverflow = document.body.style.overflow;
    this.previousBodyOverscrollBehavior = document.body.style.overscrollBehavior;
    this.previousBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    const bodyPaddingRight = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
    const supportsStableScrollbarGutter = window.CSS?.supports?.('scrollbar-gutter: stable') === true;
    document.documentElement.style.scrollBehavior = 'auto';
    document.documentElement.style.scrollbarGutter = 'stable';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    if (scrollbarWidth > 0 && !supportsStableScrollbarGutter) {
      document.body.style.paddingRight = `${bodyPaddingRight + scrollbarWidth}px`;
    }
    document.addEventListener('touchstart', this.boundModalTouchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', this.boundModalScrollBlock, { passive: false, capture: true });
    document.addEventListener('wheel', this.boundModalScrollBlock, { passive: false, capture: true });
  }

  unlockScroll() {
    const y = this.scrollLockY || 0;
    document.removeEventListener('touchstart', this.boundModalTouchStart, true);
    document.removeEventListener('touchmove', this.boundModalScrollBlock, true);
    document.removeEventListener('wheel', this.boundModalScrollBlock, true);
    this.modalTouchX = 0;
    this.modalTouchY = 0;
    document.documentElement.style.overflow = this.previousDocumentOverflow || '';
    document.documentElement.style.overscrollBehavior = this.previousDocumentOverscrollBehavior || '';
    document.documentElement.style.scrollbarGutter = this.previousDocumentScrollbarGutter || '';
    document.body.style.overflow = this.previousBodyOverflow || '';
    document.body.style.overscrollBehavior = this.previousBodyOverscrollBehavior || '';
    document.body.style.paddingRight = this.previousBodyPaddingRight || '';
    if (Math.abs((window.scrollY || document.documentElement.scrollTop || 0) - y) > 1) {
      window.scrollTo(0, y);
    }
    document.documentElement.style.scrollBehavior = this.previousScrollBehavior || '';
  }

  captureModalTouchStart(event) {
    if (!this.modal?.classList.contains('is-open') && !this.buyingGuideModal?.classList.contains('is-open')) return;
    const touch = event.touches?.[0];
    this.modalTouchX = touch ? touch.clientX : 0;
    this.modalTouchY = touch ? touch.clientY : 0;
  }

  blockModalBackgroundScroll(event) {
    if (this.buyingGuideModal?.classList.contains('is-open')) {
      const guideTarget = event.target instanceof Element ? event.target : null;
      if (!guideTarget || !this.buyingGuideModal.contains(guideTarget)) {
        event.preventDefault();
        return;
      }

      let guideScrollable = guideTarget.closest('.hp-buying-guide-modal__body, .hp-buying-guide-modal__content');
      while (
        guideScrollable instanceof HTMLElement
        && guideScrollable !== this.buyingGuideModal
        && guideScrollable.scrollHeight <= guideScrollable.clientHeight
      ) {
        guideScrollable = guideScrollable.parentElement?.closest?.('.hp-buying-guide-modal__body, .hp-buying-guide-modal__content');
      }
      if (!(guideScrollable instanceof HTMLElement) || guideScrollable.scrollHeight <= guideScrollable.clientHeight) {
        event.preventDefault();
      }
      return;
    }

    if (!this.modal?.classList.contains('is-open')) return;

    const target = event.target instanceof Element ? event.target : null;
    if (!target || !this.modal.contains(target)) {
      event.preventDefault();
      return;
    }

    if (event.type === 'touchmove' && this.modal.getAttribute('data-hp-modal-view') === 'images' && target.closest('.hp-product-modal__gallery, .hp-product-modal__media')) {
      const touch = event.touches?.[0];
      if (touch && this.isModalTrackSwipeLayout()) {
        const nativeDeltaX = this.modalTouchX ? touch.clientX - this.modalTouchX : 0;
        const nativeDeltaY = this.modalTouchY ? touch.clientY - this.modalTouchY : 0;
        if (Math.abs(nativeDeltaX) > 6 && Math.abs(nativeDeltaX) > Math.abs(nativeDeltaY)) return;
      }
      const swipe = this.modalImageSwipe;
      if (touch && swipe) {
        const deltaX = touch.clientX - swipe.startX;
        const deltaY = touch.clientY - swipe.startY;
        if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) return;
      }
    }

    if (event.type === 'wheel' && this.modal.getAttribute('data-hp-modal-view') === 'images' && target.closest('.hp-product-modal__gallery, .hp-product-modal__media')) {
      const deltaX = event.deltaX || 0;
      const deltaY = event.deltaY || 0;
      if (Math.abs(deltaX) > 6 && Math.abs(deltaX) > Math.abs(deltaY)) return;
    }

    let scrollable = target.closest('.hp-product-modal__content, .hp-product-modal__details, .hp-product-modal__description');
    while (scrollable instanceof HTMLElement && scrollable !== this.modal && scrollable.scrollHeight <= scrollable.clientHeight) {
      scrollable = scrollable.parentElement?.closest?.('.hp-product-modal__content, .hp-product-modal__details, .hp-product-modal__description');
    }
    if (!(scrollable instanceof HTMLElement) || scrollable.scrollHeight <= scrollable.clientHeight) {
      event.preventDefault();
      return;
    }

    let deltaY = 0;
    if (event.type === 'wheel') {
      deltaY = event.deltaY || 0;
    } else if (event.type === 'touchmove') {
      const touch = event.touches?.[0];
      if (!touch) {
        event.preventDefault();
        return;
      }
      deltaY = this.modalTouchY ? this.modalTouchY - touch.clientY : 0;
      this.modalTouchY = touch.clientY;
    }

    const atTop = scrollable.scrollTop <= 0;
    const atBottom = Math.ceil(scrollable.scrollTop + scrollable.clientHeight) >= scrollable.scrollHeight;
    if ((deltaY < 0 && atTop) || (deltaY > 0 && atBottom)) event.preventDefault();
  }

  formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const currency = this.getAttribute('data-currency-code') || 'SGD';
    try {
      return new Intl.NumberFormat('en-SG', {
        style: 'currency',
        currency,
        currencyDisplay: 'code',
      }).format(amount);
    } catch (error) {
      return `${currency}\u00a0${amount.toFixed(2)}`;
    }
  }

  formatPriceInputLabel(value) {
    const cents = this.normalizePriceInput(value);
    if (!Number.isFinite(cents)) return '';
    const amount = cents / 100;
    const formatted = Number.isInteger(amount) ? String(amount) : amount.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    return `$${formatted}`;
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

if (!customElements.get('hp-collection')) {
  customElements.define('hp-collection', HpCollection);
}
